import {
  GoogleMap,
  StreetViewPanorama,
  Marker,
  InfoWindow,
  StreetViewService,
  Circle,
} from '@react-google-maps/api'
import * as Sentry from '@sentry/react'
import React, { useState, useCallback, useMemo, useEffect, useRef, useContext } from 'react'
import useMedia from 'react-use/lib/useMedia'
import styled from 'styled-components'
import { format, parseISO } from 'date-fns'
import { entity, equals, query, string } from '~/prix'
import asGeoJson from '~/prix/functions/asGeoJson'
import useItem from '~/prix/react/hooks/item'
import Handle from '~/prix/react/components/handle'
import Modal from '~/prix/react/components/modal'
import useItems from '~/prix/react/hooks/items'
import useAction from '~/prix/react/hooks/action'
import List from '~/components/list'
import AlertIcon from '~/components/icons/ui/16px_alert-2.svg'
import legalEntityAttendanceListDefinition from '~/packages/legalEntityAttendance/list/legalEntityAttendanceList.definition'
import legalEntityRevalidateLocationDefinition, {
  type RevalidateLocationOutput,
} from '~/packages/legalEntity/revalidateLocation/legalEntityRevalidateLocation.definition'
import legalEntityIndividualListQuery from '~/packages/legalEntityIndividual/list/legalEntityIndividualList.query'
import cnaeListQuery from '~/packages/cnae/list/legalEntityCnaeList.query'
import DataForm from './legalEntityReadOneForm'
import BackArrowIcon from '~/meta/angle-left-arrow.svg'
import theme, { colors, complementarColors } from '~/design'
import PulseLoader from 'react-spinners/PulseLoader'
import LoadingMessage from '~/prix/react/components/loadingMessage'
import courseRegisterIndividualListQuery from '~/packages/courseRegisterIndividual/list/courseRegisterIndividualList.query'
import useGoogleMaps from '~/prix/react/hooks/googleMaps'
import { calculateGooglePovBetweenPositions } from '~/prix/utils/googlePov'

import * as turf from '@turf/helpers'
import calculateDistance from '@turf/distance'
import { formatDistance } from '~/prix/utils/types/number'

import MapPinIcon from '~/components/icons/maps/32px_map-pin.svg'
import AttendanceImageIcon from '~/components/icons/multimedia/24px_image-02.svg'
import RightArrowIcon from '~/components/icons/arrows/16px_minimal-right.svg'
import LeftArrowIcon from '~/components/icons/arrows/16px_minimal-left.svg'
import RemoveIcon from '~/components/icons/ui/24px_bold-remove.svg'
import { maskNumbers, masks } from '~/prix/utils/types/mask'
import { scrollContext } from '~/prix/react/components/scrollContext'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;

  article {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 6.5rem;
    row-gap: 3.5rem;
    flex: 1;
    margin: 1rem 32px;
    color: ${({ theme }) => theme.colors.darkGrey};
    padding-bottom: 75px;
    justify-items: center;
    z-index: 5;
    position: relative;

    h1 {
      font-size: 1.2rem;
      margin-top: 0;
      margin-bottom: 3px;
      font-weight: bold;
      color: ${({ theme }) => theme.colors.primary};
    }

    span b {
      font-weight: bold;
      color: ${({ theme }) => theme.colors.primary};
    }

    .info {
      display: flex;
      flex-direction: column;

      .span-info {
        padding: 2px 0px 2px 0px;
      }
    }

    .map {
      margin-top: 0;
      height: 100%;
      width: 100%;
      box-sizing: border-box;
      justify-self: stretch;
      grid-column: 1 / -1;

      .map-content {
        width: calc(100% - 24px);
        margin: 0 auto;
        padding-top: 20px;

        @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
          padding-top: 12px;
        }
      }
    }

    @media (max-width: 1290px) {
      grid-template-columns: 1fr;
      padding: 0 16px 64px 16px;
      margin: 0;

      .map .map-content {
        width: 100%;
      }
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      row-gap: 1.5rem;
      padding: 0 12px 12px 12px;
    }
  }
`

const StreetViewInfoContainer = styled.div`
  margin-top: 5px;
  display: flex;
  font-weight: bold;
`

const StreetViewInfoContent = styled.span`
  padding-left: 5px;
`

const CardReadOne = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  flex: 1;
  margin: 0;

  @media (max-width: 1290px) {
    margin: 0;
  }
`

const CardReadOneTitle = styled.div`
  display: flex;
  flex: 1;
  margin: 0;
  border-radius: 8px 8px 0px 0px;
  background: linear-gradient(135deg, #0f438a 0%, #1b5ab0 100%);
  padding: 25px;
  width: 100%;
  box-shadow: none;
  height: 150px;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  position: relative;
  gap: 14px;

  button {
    height: 3rem;
    width: 3rem;
    padding: 8px;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;

    border: none;
    border-radius: 100px;
    background: #005eb8;

    svg {
      margin-top: 2px;
      fill: #fff;
    }
  }

  .header-content {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    align-items: flex-start;
  }

  h3 {
    color: #fff;
    font-weight: 700;
    line-height: 1.2;
    font-size: 1.85rem;
    margin: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    padding: 15px;
    align-items: center;

    h3 {
      font-size: 1.2rem;
    }
  }
`

const LoadingWrapper = styled.div`
  display: flex;
  flex: 1;
  align-self: center;
  justify-content: center;
  height: 100%;

  .center {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    .loader {
      display: flex;
      height: 50px;
      flex-direction: row;
      align-items: center;
    }

    .message {
      display: flex;
      height: 50px;
      flex-direction: row;
      font-size: 16px;
      text-shadow: 0 5px 5px rgba(0, 0, 0, 0.05);

      @media screen and (max-width: 768px) {
        max-width: 280px;
        text-align: center;
      }
    }
  }
`

const MapLoadingState = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0px 10px 20px 0px rgba(20, 46, 82, 0.12);

  .center {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  .loader {
    display: flex;
    height: 50px;
    flex-direction: row;
    align-items: center;
  }

  .message {
    display: flex;
    font-size: 16px;
    text-align: center;
  }
`

const ListWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  box-shadow: 0px 10px 20px 0px rgba(20, 46, 82, 0.2);
  border-radius: 12px;
  min-width: 0;

  @media (max-width: 1024px) {
    justify-self: stretch;

    > div {
      width: 100% !important;
      margin-left: 0 !important;
    }
  }
`

const CompanyLocationInfoWindowContainer = styled.div`
  max-width: 320px;
  padding: 4px 2px;
  color: #17324d;
  font-family: 'Figtree', Arial, sans-serif;
  font-size: 14px;
  line-height: 1.4;
`

const CompanyLocationInfoWindowTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #0f438a;
  margin-bottom: 6px;
`

const CompanyLocationInfoWindowRow = styled.div`
  margin-top: 4px;
`

const CompanyLocationInfoWindowLink = styled.a`
  display: inline-block;
  margin-top: 8px;
  color: #005eb8;
  font-weight: 600;
  text-decoration: none;
`

const DataFormWrapper = styled.div`
  width: 100%;
  grid-column: 1 / -1;
`

const CardEntitiesListMeta = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const CardEntitiesListTag = styled.span`
  font-size: 12px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 999px;
  padding: 4px 10px;
  color: #fff;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    font-size: 10px;
  }
`

const StreetViewControls = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`

const StreetViewButton = styled.button`
  height: 2.5rem;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: #005eb8;
  color: #fff;
  cursor: pointer;
  box-shadow: 0px 4px 10px rgba(20, 46, 82, 0.15);
  transition:
    filter 120ms ease-in-out,
    transform 120ms ease-in-out;

  &:hover {
    filter: brightness(1.05);
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const AttendanceImageModalContent = styled.div`
  display: flex;
  position: relative;
  width: auto;
  min-height: min(70vh, 520px);
  max-height: 85vh;
  align-items: center;
  justify-content: center;
  background: #fff;
  padding: 16px;
  border-radius: 8px;
`

const AttendanceImage = styled.img<{ $isLoading?: boolean }>`
  max-width: 100%;
  max-height: calc(85vh - 32px);
  object-fit: contain;
  border-radius: 6px;
  opacity: ${({ $isLoading }) => ($isLoading ? 0 : 1)};
  transition: opacity 120ms ease-in-out;
`

const AttendanceImageLoader = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

const AttendanceImageNavigationButton = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: 23px;' : 'right: 23px;')}
  z-index: 2;
  display: flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.68);
  cursor: pointer;
  transform: translateY(-50%);

  &:hover {
    background: #fff;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  svg path {
    fill: #17324d;
    stroke: #17324d;
    stroke-width: 0.6px;
    stroke-linejoin: round;
  }
`

const AttendanceImageCloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
  z-index: 3;
  display: flex;
  background-color: ${theme.colors.primary};
  padding: 10px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;

  svg path {
    fill: #fff;
  }
`

const AttendanceImageCounter = styled.div`
  position: absolute;
  left: 50%;
  bottom: 12px;
  z-index: 2;
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.9);
  color: #17324d;
  font-size: 12px;
  font-weight: 700;
  transform: translateX(-50%);
`

export interface LegalEntityReadOne {
  id: string
  onClose?: () => void
  setLegalEntityName?: any
  isSentinel?: boolean
  hideHeader?: boolean
  attendanceDataSourceId?: number
}

interface SelectedAttendanceImages {
  urls: string[]
  index: number
}

const relationshipAux = {
  selfReported: 'Autorreferido',
  partner: 'Sócio',
  collaborator: 'Colaborador',
  unknown: 'Desconhecido',
}

const APP_SEBRAE_V2_DATA_SOURCE_ID = 8
const MONITOR_EMISSOR_DATA_SOURCE_ID = 7
const MAP_LOCATION_WAIT_TIMEOUT_MS = 4000
const STREET_VIEW_DEFAULT_ZOOM = 0
const STREET_VIEW_MINIMUM_LOOK_AT_PITCH = -6
const STREET_VIEW_PANORAMA_POSITION_TOLERANCE_METERS = 12
const STREET_VIEW_PANORAMA_POSITION_FALLBACK_TIMEOUT_MS = 900

interface LatLngLiteral {
  lat: number
  lng: number
}

interface RevalidatedStreetViewLocation {
  legalEntityId: string
  coordinates: LatLngLiteral
}

interface AttendanceGeoPoint {
  position: LatLngLiteral
  dataSourceName: string | null
  agentName: string | null
  startDateFormatted: string | null
  justify: string | null
  markerColor: string
}

type StreetViewTarget =
  | {
      type: 'company'
      requestedCoordinates: LatLngLiteral
      lookAtCoordinates: LatLngLiteral
      fallbackPointOfView?: google.maps.StreetViewPov | null
      scrollToTop?: boolean
    }
  | {
      type: 'attendance'
      requestedCoordinates: LatLngLiteral
      lookAtCoordinates: LatLngLiteral
      attendanceIndex: number
      attendancePoint: AttendanceGeoPoint
      scrollToTop?: boolean
    }

interface RevalidatedGoogleResultCandidate {
  latitude: number
  longitude: number
  source?: string
  placeId?: string
  formattedAddress?: string
  types?: string[]
  matchedByQueries?: string[]
  partialMatch?: boolean
  locationType?: string
  confidenceScore?: number
  confidenceLabel?: string
  addressComponents?: Array<{
    types?: string[]
  }>
}

type CompanyPlacesResult = RevalidateLocationOutput['placesResults'][number]
type CompanyNormalizedLocationResult = RevalidateLocationOutput['results'][number]

const attendanceMarkerBaseColors = [
  '#005EB8',
  '#00A65A',
  '#F39C12',
  '#8E44AD',
  '#2E86DE',
  '#7CB342',
  '#00838F',
  '#455A64',
  '#9E9D24',
  '#1ABC9C',
  '#5D6D7E',
  '#AF7AC5',
  '#2471A3',
  '#229954',
  '#B7950B',
]

const getAttendanceMarkerColorByIndex = (index: number) =>
  attendanceMarkerBaseColors[index % attendanceMarkerBaseColors.length]

const toTitleCase = (value: string): string => {
  if (!value) return ''
  return value
    .toLowerCase()
    .split(' ')
    .filter(part => part.length > 0)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const formatConfidenceLabel = (value: string | undefined) => {
  if (value === 'high') return 'Alta'
  if (value === 'medium') return 'Média'
  if (value === 'low') return 'Baixa'
  return undefined
}

const formatBusinessStatus = (value: string | undefined) => {
  if (value === 'OPERATIONAL') return 'Em operação'
  if (value === 'CLOSED_TEMPORARILY') return 'Fechado temporariamente'
  if (value === 'CLOSED_PERMANENTLY') return 'Fechado permanentemente'
  return value ? toTitleCase(value.replace(/_/g, ' ')) : undefined
}

const placeTypeLabelMap: Record<string, string> = {
  restaurant: 'Restaurante',
  cafe: 'Cafeteria',
  bakery: 'Padaria',
  bar: 'Bar',
  corporate_office: 'Escritório',
  school: 'Escola',
  university: 'Universidade',
  hospital: 'Hospital',
  pharmacy: 'Farmácia',
  supermarket: 'Supermercado',
  shopping_mall: 'Shopping',
  gas_station: 'Posto de combustível',
  bank: 'Banco',
  atm: 'Caixa eletrônico',
  tourist_attraction: 'Ponto turístico',
  park: 'Parque',
  gym: 'Academia',
}

const formatPlaceType = (value: string | undefined) => {
  if (!value) return undefined
  return placeTypeLabelMap[value] ?? undefined
}

const formatLocationSource = (value: string | undefined) => {
  if (value === 'attendanceHistoryCluster') return 'Histórico de atendimentos'
  if (value === 'placesTextSearchPro') return 'Google Places'
  if (value === 'googleGeocoding') return 'Google Geocoding'
  return undefined
}

const escapeHtml = (value: string) =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')

const buildAttendanceInfoWindowHtml = (point: AttendanceGeoPoint): string => {
  const displayAgentName = point.agentName ? toTitleCase(point.agentName) : ''

  return `
    <div class="prix-info-window" style="font-family: 'Figtree', Arial, sans-serif; font-size: 14px; line-height: 1.35; max-width: 280px; font-weight: 400; padding-right: 25px;">
      <div><strong>Fonte:</strong> ${escapeHtml(point.dataSourceName ?? '')}</div>
      ${displayAgentName ? `<div><strong>Agente:</strong> ${escapeHtml(displayAgentName)}</div>` : ''}
      ${point.startDateFormatted ? `<div><strong>Data:</strong> ${escapeHtml(point.startDateFormatted)}</div>` : ''}
    </div>
  `
}

const AttendanceInfoWindowContent = ({
  onOpenStreetView,
  point,
}: {
  onOpenStreetView?: () => void
  point: AttendanceGeoPoint
}) => {
  const displayAgentName = point.agentName ? toTitleCase(point.agentName) : ''

  return (
    <div
      className='prix-info-window'
      data-attendance-map-info-window={onOpenStreetView ? 'true' : undefined}
      onClick={onOpenStreetView}
      onKeyDown={event => {
        if (!onOpenStreetView) return
        if (event.key !== 'Enter' && event.key !== ' ') return

        event.preventDefault()
        onOpenStreetView()
      }}
      role={onOpenStreetView ? 'button' : undefined}
      style={{
        cursor: onOpenStreetView ? 'pointer' : undefined,
        fontFamily: "'Figtree', Arial, sans-serif",
        fontSize: 14,
        fontWeight: 400,
        lineHeight: 1.35,
        maxWidth: 280,
        paddingRight: 25,
      }}
      tabIndex={onOpenStreetView ? 0 : undefined}
    >
      <div>
        <strong>Fonte:</strong> {point.dataSourceName ?? ''}
      </div>
      {displayAgentName ? (
        <div>
          <strong>Agente:</strong> {displayAgentName}
        </div>
      ) : null}
      {point.startDateFormatted ? (
        <div>
          <strong>Data:</strong> {point.startDateFormatted}
        </div>
      ) : null}
    </div>
  )
}

const getCompanyLocationInfoData = ({
  addressLabel,
  normalizedResult,
  placesResult,
}: {
  addressLabel?: string
  normalizedResult?: CompanyNormalizedLocationResult | null
  placesResult?: CompanyPlacesResult | null
}) => {
  const effectivePlacesResult =
    normalizedResult?.source === 'placesTextSearchPro' ? placesResult : null
  const normalizedConfidenceLabel = formatConfidenceLabel(normalizedResult?.confidenceLabel)
  const placesConfidenceLabel = formatConfidenceLabel(effectivePlacesResult?.confidenceLabel)
  const primaryType = formatPlaceType(
    effectivePlacesResult?.primaryType ?? effectivePlacesResult?.types?.[0],
  )
  const businessStatus = formatBusinessStatus(effectivePlacesResult?.businessStatus)
  const preferredAddress = effectivePlacesResult?.formattedAddress ?? addressLabel
  const uniqueAddress =
    preferredAddress && addressLabel && preferredAddress === addressLabel
      ? preferredAddress
      : preferredAddress
  const title =
    effectivePlacesResult?.displayName ?? normalizedResult?.displayName ?? 'Localização da Empresa'
  const confidenceText = placesConfidenceLabel
    ? `${placesConfidenceLabel}${
        effectivePlacesResult?.confidenceScore != null
          ? ` (${Math.round(effectivePlacesResult.confidenceScore)})`
          : ''
      }`
    : normalizedConfidenceLabel
      ? `${normalizedConfidenceLabel}${
          normalizedResult?.confidenceScore != null
            ? ` (${Math.round(normalizedResult.confidenceScore)})`
            : ''
        }`
      : undefined

  return {
    title,
    addressLabel: uniqueAddress,
    confidenceText,
    sourceLabel: formatLocationSource(normalizedResult?.source),
    primaryType,
    businessStatus,
    googleMapsUri: effectivePlacesResult?.googleMapsUri,
  }
}

const buildCompanyLocationInfoHtml = ({
  addressLabel,
  normalizedResult,
  placesResult,
}: {
  addressLabel?: string
  normalizedResult?: CompanyNormalizedLocationResult | null
  placesResult?: CompanyPlacesResult | null
}) => {
  const info = getCompanyLocationInfoData({
    addressLabel,
    normalizedResult,
    placesResult,
  })

  const rows = [
    info.addressLabel ? ['Endereço', info.addressLabel] : null,
    info.confidenceText ? ['Confiança', info.confidenceText] : null,
    info.sourceLabel ? ['Fonte', info.sourceLabel] : null,
    info.primaryType ? ['Tipo', info.primaryType] : null,
    info.businessStatus ? ['Status', info.businessStatus] : null,
  ].filter(Boolean) as Array<[string, string]>

  const rowsHtml = rows
    .map(
      ([label, value]) =>
        `<div style="margin-top:4px;"><strong>${escapeHtml(label)}:</strong> ${escapeHtml(value)}</div>`,
    )
    .join('')

  const linkHtml = info.googleMapsUri
    ? `<a href="${escapeHtml(info.googleMapsUri)}" target="_blank" rel="noreferrer" style="display:inline-block;margin-top:8px;color:#005eb8;font-weight:600;text-decoration:none;">Abrir no Google Maps</a>`
    : ''

  return `
    <div style="max-width:320px;padding:4px 2px;color:#17324d;font-family:'Figtree',Arial,sans-serif;font-size:14px;line-height:1.4;">
      <div style="font-size:15px;font-weight:700;color:#0f438a;margin-bottom:6px;">${escapeHtml(info.title)}</div>
      ${rowsHtml}
      ${linkHtml}
    </div>
  `
}

const scoreRevalidatedGoogleResult = (result: RevalidatedGoogleResultCandidate) => {
  let score = 0
  const types = new Set(result.types ?? [])
  const matchedByQueries = new Set(result.matchedByQueries ?? [])
  const addressComponentTypes = new Set(
    (result.addressComponents ?? []).flatMap(component => component.types ?? []),
  )

  if (types.has('street_address')) score += 120
  if (types.has('premise')) score += 90
  if (types.has('subpremise')) score += 80
  if (types.has('establishment')) score += 40
  if (types.has('route')) score -= 30

  if (result.locationType === 'ROOFTOP') score += 70
  if (result.locationType === 'RANGE_INTERPOLATED') score += 30
  if (result.locationType === 'GEOMETRIC_CENTER') score -= 10
  if (result.locationType === 'APPROXIMATE') score -= 20

  if (addressComponentTypes.has('street_number')) score += 35
  if (addressComponentTypes.has('route')) score += 20
  if (addressComponentTypes.has('postal_code')) score += 10

  if (matchedByQueries.has('named')) score += 15
  if (result.partialMatch === true) score -= 15

  return score
}

const isReliableRevalidatedGoogleResult = (result: RevalidatedGoogleResultCandidate) =>
  result.confidenceLabel === 'high' ||
  result.confidenceLabel === 'medium' ||
  (result.confidenceScore ?? 0) >= 75

const pickBestRevalidatedResult = (
  results: RevalidatedGoogleResultCandidate[] | undefined,
): RevalidatedGoogleResultCandidate | null => {
  if (!results?.length) {
    return null
  }

  const [bestResult] = results
    .filter(isReliableRevalidatedGoogleResult)
    .sort((left, right) => scoreRevalidatedGoogleResult(right) - scoreRevalidatedGoogleResult(left))

  return bestResult ?? null
}

const getRevalidatedResultCoordinates = (
  result: RevalidatedGoogleResultCandidate | null,
): LatLngLiteral | null => {
  if (!result || !Number.isFinite(result.latitude) || !Number.isFinite(result.longitude)) {
    return null
  }

  return {
    lat: result.latitude,
    lng: result.longitude,
  }
}

const getDebugCoordinates = (coordinates: LatLngLiteral | null | undefined) =>
  coordinates
    ? {
        lat: Number(coordinates.lat.toFixed(6)),
        lng: Number(coordinates.lng.toFixed(6)),
      }
    : null

const getStreetViewCoordinatesKey = (coordinates: LatLngLiteral | null | undefined) =>
  coordinates ? `${coordinates.lat.toFixed(6)},${coordinates.lng.toFixed(6)}` : null

const getStreetViewTargetKey = (target: StreetViewTarget) => {
  const requestedCoordinatesKey = getStreetViewCoordinatesKey(target.requestedCoordinates)
  const lookAtCoordinatesKey = getStreetViewCoordinatesKey(target.lookAtCoordinates)
  const targetIdentifier = target.type === 'attendance' ? target.attendanceIndex : 'company'

  return `${target.type}:${targetIdentifier}:${requestedCoordinatesKey}:${lookAtCoordinatesKey}`
}

const getStrictStreetViewSources = (googleApi: typeof google) => [
  googleApi.maps.StreetViewSource.GOOGLE,
  googleApi.maps.StreetViewSource.OUTDOOR,
]

const getDebugGoogleLatLngCoordinates = (coordinates: google.maps.LatLng | null | undefined) =>
  coordinates ? getDebugCoordinates(coordinates.toJSON()) : null

const getDebugDistanceMeters = (distanceMeters: number | null | undefined) =>
  typeof distanceMeters === 'number' && Number.isFinite(distanceMeters)
    ? Number(distanceMeters.toFixed(2))
    : null

const calculateGoogleLatLngDistanceMeters = (
  origin: google.maps.LatLng,
  target: google.maps.LatLng,
) =>
  calculateDistance(
    turf.point([origin.lng(), origin.lat()]),
    turf.point([target.lng(), target.lat()]),
    'kilometers',
  ) * 1000

const getDebugStreetViewPointOfView = (pointOfView: google.maps.StreetViewPov | null) =>
  pointOfView
    ? {
        heading: Number(pointOfView.heading.toFixed(2)),
        pitch: Number(pointOfView.pitch.toFixed(2)),
      }
    : null

const stringifyStreetViewDebugDetails = (details: unknown) => {
  try {
    return JSON.stringify(details)
  } catch {
    return '[unserializable street view debug details]'
  }
}

const logStreetViewLocationDebug = (
  label: string,
  details: Record<string, unknown>,
  cacheEvents?: RevalidateLocationOutput['sourceSelection']['cacheEvents'],
) => {
  if (process.env.NODE_ENV === 'production') return

  console.groupCollapsed(`[StreetView] ${label}`)
  console.info(`[StreetView] ${label} detalhes`, stringifyStreetViewDebugDetails(details))
  console.info(details)
  if (cacheEvents?.length) {
    console.info(`[StreetView] ${label} cacheEvents`, stringifyStreetViewDebugDetails(cacheEvents))
    console.table(
      cacheEvents.map(event => ({
        source: event.source,
        status: event.status,
        resultCount: event.resultCount,
        placesResultCount: event.placesResultCount,
        succeededSource: event.succeededSource ?? null,
        cacheable: event.cacheable ?? null,
        externalRequest: event.externalRequest ?? null,
      })),
    )
  }
  console.groupEnd()
}

const getSilentLocationRefreshErrorDetails = (error: unknown) => {
  const maybeWrappedError = error as {
    error?: unknown
    response?: {
      data?: {
        error?: unknown
      }
    }
  }

  return (maybeWrappedError.error ?? maybeWrappedError.response?.data?.error ?? error) as {
    key?: string
    code?: string | number
    statusCode?: string | number
    name?: string
    message?: string
  }
}

const isExpectedSilentLocationRefreshError = (error: unknown) => {
  const maybeError = getSilentLocationRefreshErrorDetails(error)

  return (
    maybeError.key === 'abort' ||
    maybeError.key === 'tooManyRequests' ||
    maybeError.code === 429 ||
    maybeError.code === '429' ||
    maybeError.code === 499 ||
    maybeError.code === '499' ||
    maybeError.statusCode === 429 ||
    maybeError.statusCode === '429' ||
    maybeError.statusCode === 499 ||
    maybeError.statusCode === '499' ||
    maybeError.name === 'CanceledError' ||
    maybeError.name === 'AbortError' ||
    maybeError.message === 'canceled'
  )
}

const reportSilentLocationRefreshError = (error: unknown, context: Record<string, unknown>) => {
  if (isExpectedSilentLocationRefreshError(error)) {
    console.info('Atualização silenciosa da localização da empresa interrompida.', context)
    return
  }

  console.error('Falha ao atualizar silenciosamente a localização da empresa.', context, error)

  Sentry.captureException(error, {
    extra: context,
  })
}

export default function LegalEntityReadOne({
  id,
  onClose,
  setLegalEntityName,
  isSentinel = false,
  hideHeader = false,
  attendanceDataSourceId,
}: LegalEntityReadOne) {
  const { google, isLoading: isLoadingGoogle } = useGoogleMaps()
  const modalScrollReference = useContext(scrollContext)
  const [status, setStatus] = useState<string | undefined>(undefined)
  const [selectedAttendanceImages, setSelectedAttendanceImages] =
    useState<SelectedAttendanceImages | null>(null)
  const [isAttendanceImageLoading, setIsAttendanceImageLoading] = useState(false)
  const selectedAttendanceImageUrl =
    selectedAttendanceImages?.urls[selectedAttendanceImages.index] ?? null
  const selectedAttendanceImageCount = selectedAttendanceImages?.urls.length ?? 0
  const isLargeCondition = useMedia('(max-width: 1290px)')

  const isSmallCondition = useMedia('(max-width: 840px)')

  const closeSelectedAttendanceImages = useCallback(() => {
    setSelectedAttendanceImages(null)
    setIsAttendanceImageLoading(false)
  }, [])

  const goToSelectedAttendanceImage = useCallback((direction: number) => {
    setIsAttendanceImageLoading(true)
    setSelectedAttendanceImages(current => {
      if (!current || current.urls.length < 2) return current

      return {
        ...current,
        index: (current.index + direction + current.urls.length) % current.urls.length,
      }
    })
  }, [])

  const { error, isLoading, item } = useItem(
    () =>
      query('legalEntity')
        .select({
          tradeName: entity('legalEntity').property('tradeName'),
          corporateName: entity('legalEntity').property('corporateName'),
          cnpj: entity('legalEntity').property('cnpj'),
          publicPlace: entity('legalEntity').property('publicPlace'),
          number: entity('legalEntity').property('number'),
          complement: entity('legalEntity').property('complement'),
          neighborhood: entity('legalEntity').property('neighborhood'),
          city: entity('legalEntity').property('city'),
          state: entity('legalEntity').property('state'),
          postalCode: entity('legalEntity').property('postalCode'),
          pointOnStreet: asGeoJson(entity('legalEntityGeoprocessing').property('pointOnStreet')),
          pointOnStreetDirectionAngle: entity('legalEntityGeoprocessing').property(
            'pointOnStreetDirectionAngle',
          ),
          sector: entity('legalEntity').property('sector'),
          sizeAbbreviation: entity('legalEntity').property('sizeAbbreviation'),
          openingDate: entity('legalEntity').property('openingDate'),
          email: entity('legalEntity').property('email'),
          phoneNumberOneAreaCode: entity('legalEntity').property('phoneNumber_1AreaCode'),
          phoneNumberOne: entity('legalEntity').property('phoneNumber_1'),
          phoneNumberTwoAreaCode: entity('legalEntity').property('phoneNumber_2AreaCode'),
          phoneNumberTwo: entity('legalEntity').property('phoneNumber_2'),
        })
        .join({
          current: entity('legalEntity').property('id'),
          target: entity('legalEntityGeoprocessing').property('legalEntityId'),
        })
        .where(equals(entity('legalEntity').property('id'), string().value(id)))
        .limit(1),
    [id],
  )

  const attendanceListInput = useMemo(() => {
    const input: { legalEntityId: string; dataSourceId?: number } = {
      legalEntityId: id,
      dataSourceId: attendanceDataSourceId ?? APP_SEBRAE_V2_DATA_SOURCE_ID,
    }

    return input
  }, [attendanceDataSourceId, id])

  const attendanceList = useAction(
    legalEntityAttendanceListDefinition,
    attendanceListInput,
    [id, attendanceDataSourceId],
    {
      autoLoad: isLoading === false,
    },
  )

  const revalidatedLocation = useAction(
    legalEntityRevalidateLocationDefinition,
    { id, source: 'placesTextSearchPro' },
    [id],
    {
      autoLoad: false,
    },
  )

  const [revalidatedStreetViewLocation, setRevalidatedStreetViewLocation] =
    useState<RevalidatedStreetViewLocation | null>(null)
  const [hasReleasedMapLocationGate, setHasReleasedMapLocationGate] = useState(false)

  const revalidatedStreetViewCoordinates = useMemo(
    () =>
      revalidatedStreetViewLocation?.legalEntityId === id
        ? revalidatedStreetViewLocation.coordinates
        : null,
    [id, revalidatedStreetViewLocation],
  )

  const cityState = useMemo(() => {
    const parts = [item?.city, item?.state].filter(value => Boolean(value && String(value).trim()))
    return parts.length > 0 ? parts.join(' - ') : '-'
  }, [item?.city, item?.state])

  const courseRegisterIndividualList = useItems(() => courseRegisterIndividualListQuery(id), [id], {
    cache: 60 * 60 * 8,
    autoLoad: isLoading === false,
  })

  const formattedCourseRegisterIndividualList = useMemo(() => {
    const attendance = courseRegisterIndividualList.items?.map(item => ({
      courseProductCode: item.courseProductCode,
      courseProductName: item.courseProductName,
      courseProductType: item.courseProductType,
      courseProductPortfolioSasCode: item.courseProductPortfolioSasCode,
      isPortfolio: item.isActivePortfolio ? 'Ativo' : 'Inativo',
      registerDate:
        item?.registerDate === null
          ? null
          : format(parseISO(item.registerDate as string), 'dd/MM/yyyy'),
      courseCompletionPercentage: item.courseCompletionPercentage
        ? `${item.courseCompletionPercentage.toString().replace('.', ',')}%`
        : 'Sem dados',
    }))

    return attendance
  }, [attendanceList])

  useEffect(() => {
    if (setLegalEntityName) {
      setLegalEntityName(
        item?.tradeName ? item?.tradeName : item?.corporateName ? item?.corporateName : '',
      )
    }
  }, [item])

  const attendanceGeoPoints = useMemo<AttendanceGeoPoint[]>(() => {
    const items = attendanceList.result?.items as
      | Array<{
          latitude: number | null
          longitude: number | null
          dataSourceName?: string | null
          agentName?: string | null
          startDate?: string | null
          justify?: string | null
        }>
      | undefined

    if (!items) return []

    const seen = new Set<string>()
    const points: AttendanceGeoPoint[] = []

    for (const record of items) {
      const lat = record?.latitude != null ? Number(record.latitude) : NaN
      const lng = record?.longitude != null ? Number(record.longitude) : NaN
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue

      const key = `${lat.toFixed(6)},${lng.toFixed(6)}`
      if (seen.has(key)) continue
      seen.add(key)

      const startDateFormatted = record?.startDate
        ? format(parseISO(record.startDate as string), 'dd/MM/yyyy')
        : null

      points.push({
        position: { lat, lng },
        dataSourceName: (record as any)?.dataSourceName ?? null,
        agentName: (record as any)?.agentName ?? null,
        startDateFormatted,
        justify: (record as any)?.justify ?? null,
        markerColor: getAttendanceMarkerColorByIndex(points.length),
      })
    }

    return points
  }, [attendanceList.result?.items])

  const darkenHexColor = (hex: string, factor: number = 0.75): string => {
    const normalized = hex.replace('#', '')
    const num = parseInt(normalized, 16)
    const r = Math.max(0, Math.min(255, Math.floor(((num >> 16) & 255) * factor)))
    const g = Math.max(0, Math.min(255, Math.floor(((num >> 8) & 255) * factor)))
    const b = Math.max(0, Math.min(255, Math.floor((num & 255) * factor)))
    const toHex = (v: number) => v.toString(16).padStart(2, '0').toUpperCase()
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
  }

  const createPinSvg = (outerColor: string, innerColor?: string): string => {
    const circleColor = innerColor ?? darkenHexColor(outerColor, 0.8)
    return `<svg width="21" height="34" viewBox="0 0 21 34" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 0C4.701 0 0 4.701 0 10.5 0 18.375 10.5 34 10.5 34S21 18.375 21 10.5C21 4.701 16.299 0 10.5 0z" fill="${outerColor}"/><circle cx="10.5" cy="10.5" r="4.5" fill="${circleColor}"/></svg>`
  }

  const svgToDataUrl = (svg: string): string =>
    `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`

  const attendanceMarkerIcons = useMemo(() => {
    const urls = attendanceGeoPoints.map(point => svgToDataUrl(createPinSvg(point.markerColor)))
    if (!google) return urls
    const size = new google.maps.Size(21, 34)
    const anchor = new google.maps.Point(10, 34)
    return urls.map(url => ({ url, scaledSize: size, anchor }))
  }, [google, attendanceGeoPoints])

  const getAttendanceMarkerIcon = useCallback(
    (index: number) => attendanceMarkerIcons[index],
    [attendanceMarkerIcons],
  )

  const companyMarkerIcon = useMemo(() => {
    const url = svgToDataUrl(createPinSvg('#C0392B', '#7B241C'))
    if (!google) return url

    return {
      url,
      scaledSize: new google.maps.Size(24, 38),
      anchor: new google.maps.Point(12, 38),
    }
  }, [google])

  const hexToRgba = useCallback((hex: string, alpha: number) => {
    const normalized = hex.replace('#', '')
    const bigint = parseInt(normalized, 16)
    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }, [])

  const [streetViewPanoramaInstance, setStreetViewPanoramaInstance] =
    useState<google.maps.StreetViewPanorama | null>(null)

  const [streetViewServiceInstance, setStreetViewServiceInstance] =
    useState<google.maps.StreetViewService | null>(null)

  const [selectedAttendanceIndex, setSelectedAttendanceIndex] = useState<number | null>(null)
  const [selectedAttendancePointOverride, setSelectedAttendancePointOverride] =
    useState<AttendanceGeoPoint | null>(null)
  const [isInitialInfoOpen, setIsInitialInfoOpen] = useState(false)
  const [hasInitializedPov, setHasInitializedPov] = useState(false)
  const [isStreetViewVisible, setIsStreetViewVisible] = useState<boolean>(true)
  const [streetViewInstanceKey, setStreetViewInstanceKey] = useState<number>(0)

  useEffect(() => {
    if (status === undefined) return
    if (status === 'ZERO_RESULTS') setIsStreetViewVisible(false)
  }, [status])

  const mapInfoWindowRef = useRef<google.maps.InfoWindow | null>(null)
  const mapAttendanceInfoWindowClickCleanupRef = useRef<(() => void) | null>(null)
  const mapInitialInfoWindowRef = useRef<google.maps.InfoWindow | null>(null)
  const panoramaInfoWindowRef = useRef<google.maps.InfoWindow | null>(null)
  const companyPanoramaMarkerRef = useRef<google.maps.Marker | null>(null)
  const companyPanoramaInfoWindowRef = useRef<google.maps.InfoWindow | null>(null)
  const panoramaRequestIdRef = useRef(0)
  const streetViewTargetKeyRef = useRef<string | null>(null)
  const companyPanoramaViewStateRef = useRef<{
    heading: number
    pitch: number
    zoom: number
  } | null>(null)
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const contentTopReference = useRef<HTMLDivElement | null>(null)
  const attendancePanoMarkersReference = useRef<google.maps.Marker[]>([])
  const previousLegalEntityIdReference = useRef<string | null>(null)

  const scrollContentToTop = useCallback(() => {
    requestAnimationFrame(() => {
      const scrollElement = modalScrollReference?.current

      if (scrollElement) {
        if (typeof scrollElement.scrollTo === 'function') {
          scrollElement.scrollTo({ top: 0, behavior: 'auto' })
        } else {
          scrollElement.scrollTop = 0
        }
        return
      }

      contentTopReference.current?.scrollIntoView({ block: 'start', behavior: 'auto' })
    })
  }, [modalScrollReference])

  const closeAllInfoWindows = useCallback(() => {
    mapAttendanceInfoWindowClickCleanupRef.current?.()
    mapAttendanceInfoWindowClickCleanupRef.current = null
    panoramaInfoWindowRef.current?.close()
    companyPanoramaInfoWindowRef.current?.close()
    mapInfoWindowRef.current?.close()
    mapInitialInfoWindowRef.current?.close()
  }, [])

  const clearAttendancePanoMarkers = useCallback(() => {
    attendancePanoMarkersReference.current.forEach(marker => {
      try {
        google?.maps.event.clearInstanceListeners(marker)
        marker.setMap(null)
      } catch {}
    })

    attendancePanoMarkersReference.current = []
  }, [google])

  useEffect(
    () => () => {
      mapAttendanceInfoWindowClickCleanupRef.current?.()
      mapAttendanceInfoWindowClickCleanupRef.current = null
    },
    [],
  )

  const openAttendancePanoramaInfoWindow = useCallback(
    (marker: google.maps.Marker, point: AttendanceGeoPoint) => {
      if (!google || !streetViewPanoramaInstance) return

      let info = panoramaInfoWindowRef.current
      if (info) {
        info.close()
      } else {
        info = new google.maps.InfoWindow()
        panoramaInfoWindowRef.current = info
      }
      info.setOptions({ disableAutoPan: true })
      info.setContent(buildAttendanceInfoWindowHtml(point))
      info.open({ anchor: marker, map: streetViewPanoramaInstance as unknown as google.maps.Map })
    },
    [google, streetViewPanoramaInstance],
  )

  useEffect(() => {
    if (previousLegalEntityIdReference.current === null) {
      previousLegalEntityIdReference.current = id
      return
    }

    if (previousLegalEntityIdReference.current === id) {
      return
    }

    previousLegalEntityIdReference.current = id
    panoramaRequestIdRef.current += 1
    streetViewTargetKeyRef.current = null
    companyPanoramaViewStateRef.current = null
    closeAllInfoWindows()
    clearAttendancePanoMarkers()
    setSelectedAttendanceIndex(null)
    setSelectedAttendancePointOverride(null)
    setIsInitialInfoOpen(false)
    setHasInitializedPov(false)
    setIsStreetViewVisible(true)
    setStreetViewAvailabilityByKey(new Map())
    setStatus(undefined)
  }, [id, clearAttendancePanoMarkers, closeAllInfoWindows])

  useEffect(() => {
    if (panoramaInfoWindowRef.current && selectedAttendanceIndex != null) {
      panoramaInfoWindowRef.current.close()
    }
  }, [selectedAttendanceIndex, panoramaInfoWindowRef])

  useEffect(() => {
    if (!google || !streetViewPanoramaInstance) return
    if (!isStreetViewVisible) return

    clearAttendancePanoMarkers()

    const created = attendanceGeoPoints.map((point, index) => {
      const marker = new google.maps.Marker({
        position: point.position,
        icon: getAttendanceMarkerIcon(index) as any,
      })
      marker.setMap(streetViewPanoramaInstance as unknown as google.maps.Map)

      marker.addListener('click', () => {
        setIsInitialInfoOpen(false)
        openAttendancePanoramaInfoWindow(marker, point)
      })

      return marker
    })

    attendancePanoMarkersReference.current = created

    return () => {
      created.forEach(marker => {
        google.maps.event.clearInstanceListeners(marker)
        marker.setMap(null)
      })
      attendancePanoMarkersReference.current = []
      panoramaInfoWindowRef.current?.close()
    }
  }, [
    google,
    streetViewPanoramaInstance,
    attendanceGeoPoints,
    getAttendanceMarkerIcon,
    isStreetViewVisible,
    openAttendancePanoramaInfoWindow,
    clearAttendancePanoMarkers,
  ])

  const legalEntityIndividualList = useItems(() => legalEntityIndividualListQuery(id), [id], {
    cache: 60 * 60 * 8,
    autoLoad: isLoading === false,
  })

  const formattedLegalEntityIndividualList = useMemo(() => {
    const individual = legalEntityIndividualList.items?.map(item => ({
      name: item.name,
      relationship: relationshipAux[item.relationship as unknown as keyof typeof relationshipAux],
    }))
    return individual
  }, [legalEntityIndividualList.items])

  const cnaeList = useItems(() => cnaeListQuery(id), [id], {
    cache: 60 * 60 * 8,
    autoLoad: isLoading === false,
  })

  const mainCnae = useMemo(() => {
    const cnaes = cnaeList.items
      ?.map(item => ({
        cnae_id: item.cnaeId,
        description: item.description,
        is_main: item.isMain,
        segmentDescription: item.segmentDescription,
      }))
      .filter(item => item.is_main === true)

    return cnaes
  }, [cnaeList.items])

  const storedPointOnStreetDirectionAngle = item?.pointOnStreetDirectionAngle as number | null

  const storedStreetViewCoordinates = useMemo(() => {
    const pointOnStreet =
      (item?.pointOnStreet as { coordinates: number[] } | null | undefined) ?? null

    return pointOnStreet
      ? {
          lng: pointOnStreet.coordinates[0],
          lat: pointOnStreet.coordinates[1],
        }
      : null
  }, [item?.pointOnStreet])

  useEffect(() => {
    let isCurrent = true
    let abortRequest: undefined | (() => Promise<void>)

    setRevalidatedStreetViewLocation(null)
    setHasReleasedMapLocationGate(false)

    const timeoutId = window.setTimeout(() => {
      if (!isCurrent) return
      setHasReleasedMapLocationGate(true)
    }, MAP_LOCATION_WAIT_TIMEOUT_MS)

    try {
      const { abort, resultPromise } = revalidatedLocation.callAction({
        id,
        source: 'placesTextSearchPro',
      })
      abortRequest = abort

      resultPromise
        .then(result => {
          if (!isCurrent) return

          const bestResult = pickBestRevalidatedResult(result?.results)
          const bestCoordinates = getRevalidatedResultCoordinates(bestResult)

          logStreetViewLocationDebug(
            'Resposta da revalidação de localização',
            {
              legalEntityId: id,
              requestedSource: result?.sourceSelection?.requestedSource,
              executedSources: result?.sourceSelection?.executedSources,
              succeededSource: result?.sourceSelection?.succeededSource ?? null,
              fellBack: result?.sourceSelection?.fellBack,
              usedPlacesMonthlyQuota: result?.sourceSelection?.usedPlacesMonthlyQuota,
              placesMonthlyUsageCount: result?.sourceSelection?.placesMonthlyUsageCount ?? null,
              placesMonthlyUsageLimit: result?.sourceSelection?.placesMonthlyUsageLimit ?? null,
              resultCount: result?.results?.length ?? 0,
              placesResultCount: result?.placesResults?.length ?? 0,
              selectedRevalidatedSource: bestResult?.source ?? null,
              selectedConfidenceScore: bestResult?.confidenceScore ?? null,
              selectedConfidenceLabel: bestResult?.confidenceLabel ?? null,
              selectedLocationType: bestResult?.locationType ?? null,
              selectedCoordinates: getDebugCoordinates(bestCoordinates),
            },
            result?.sourceSelection?.cacheEvents,
          )

          if (bestCoordinates) {
            setRevalidatedStreetViewLocation({
              legalEntityId: id,
              coordinates: bestCoordinates,
            })
          }

          setHasReleasedMapLocationGate(true)
        })
        .catch(error => {
          if (!isCurrent) return

          logStreetViewLocationDebug('Falha esperada ou erro na revalidação de localização', {
            legalEntityId: id,
            error: getSilentLocationRefreshErrorDetails(error),
          })

          reportSilentLocationRefreshError(error, {
            legalEntityId: id,
            source: 'legalEntityReadOne.revalidateLocation',
          })
          setHasReleasedMapLocationGate(true)
        })
        .finally(() => {
          window.clearTimeout(timeoutId)
        })
    } catch (error) {
      reportSilentLocationRefreshError(error, {
        legalEntityId: id,
        source: 'legalEntityReadOne.revalidateLocation.init',
      })
      setHasReleasedMapLocationGate(true)
      window.clearTimeout(timeoutId)
    }

    return () => {
      isCurrent = false
      window.clearTimeout(timeoutId)
      void Promise.resolve(abortRequest?.()).catch(() => undefined)
    }
  }, [id, revalidatedLocation.callAction])

  const streetViewCoordinates = useMemo(() => {
    if (revalidatedStreetViewCoordinates) {
      return revalidatedStreetViewCoordinates
    }

    if (hasReleasedMapLocationGate) {
      return storedStreetViewCoordinates
    }

    return null
  }, [revalidatedStreetViewCoordinates, hasReleasedMapLocationGate, storedStreetViewCoordinates])

  useEffect(() => {
    if (!google || !streetViewPanoramaInstance) return

    const listener = google.maps.event.addListener(
      streetViewPanoramaInstance,
      'visible_changed',
      () => {
        const isVisible = streetViewPanoramaInstance.getVisible()
        setIsStreetViewVisible(isVisible)

        if (isVisible) return

        try {
          panoramaInfoWindowRef.current?.close()
          companyPanoramaInfoWindowRef.current?.close()
        } catch {}

        const effectiveSelectedPoint =
          selectedAttendancePointOverride ??
          (selectedAttendanceIndex != null ? attendanceGeoPoints[selectedAttendanceIndex] : null)
        const target = effectiveSelectedPoint
          ? effectiveSelectedPoint.position
          : streetViewCoordinates

        if (mapInstanceRef.current && target) {
          mapInstanceRef.current.panTo(target)
          mapInstanceRef.current.setZoom(16)
        }
      },
    )

    return () => listener.remove()
  }, [
    attendanceGeoPoints,
    google,
    selectedAttendanceIndex,
    selectedAttendancePointOverride,
    streetViewCoordinates,
    streetViewPanoramaInstance,
  ])

  const isWaitingForPreferredLocation =
    !hasReleasedMapLocationGate && revalidatedStreetViewCoordinates === null

  const streetViewLocationSource = useMemo(() => {
    if (revalidatedStreetViewCoordinates) {
      return (
        pickBestRevalidatedResult(revalidatedLocation.result?.results)?.source ??
        'googleRevalidated'
      )
    }

    if (streetViewCoordinates && hasReleasedMapLocationGate) {
      return 'pointOnStreet'
    }

    if (isWaitingForPreferredLocation) {
      return 'waitingForGoogle'
    }

    return 'none'
  }, [
    hasReleasedMapLocationGate,
    isWaitingForPreferredLocation,
    revalidatedLocation.result?.results,
    revalidatedStreetViewCoordinates,
    streetViewCoordinates,
  ])

  useEffect(() => {
    logStreetViewLocationDebug('Fonte final usada no Street View', {
      legalEntityId: id,
      finalSource: streetViewLocationSource,
      coordinates: getDebugCoordinates(streetViewCoordinates),
      hasRevalidatedCoordinates: Boolean(revalidatedStreetViewCoordinates),
      hasStoredPointOnStreet: Boolean(storedStreetViewCoordinates),
      isWaitingForPreferredLocation,
    })
  }, [
    id,
    isWaitingForPreferredLocation,
    revalidatedStreetViewCoordinates,
    storedStreetViewCoordinates,
    streetViewCoordinates,
    streetViewLocationSource,
  ])

  const pointOnStreetDirectionAngle = revalidatedStreetViewCoordinates
    ? null
    : storedPointOnStreetDirectionAngle
  const companyStreetViewLookAtCoordinates = streetViewCoordinates

  useEffect(() => {
    companyPanoramaViewStateRef.current = null
    streetViewTargetKeyRef.current = null
  }, [id, streetViewCoordinates?.lat, streetViewCoordinates?.lng])

  const bestNormalizedLocationResult = useMemo(() => {
    return pickBestRevalidatedResult(
      revalidatedLocation.result?.results,
    ) as CompanyNormalizedLocationResult | null
  }, [revalidatedLocation.result])

  const bestPlacesResult = useMemo(() => {
    const placesResults = revalidatedLocation.result?.placesResults
    if (!placesResults?.length) {
      return null
    }

    if (bestNormalizedLocationResult?.source !== 'placesTextSearchPro') {
      return null
    }

    const currentPlacesPlaceId = bestNormalizedLocationResult.placeId

    if (currentPlacesPlaceId) {
      const matchingPlacesResult = placesResults.find(
        (result: CompanyPlacesResult) => result.placeId === currentPlacesPlaceId,
      )
      if (matchingPlacesResult) {
        return matchingPlacesResult
      }
    }

    return [...placesResults].sort(
      (left, right) => (right.confidenceScore ?? 0) - (left.confidenceScore ?? 0),
    )[0]
  }, [bestNormalizedLocationResult, revalidatedLocation.result])

  const companyLocationAddressLabel = useMemo(() => {
    if (bestNormalizedLocationResult?.formattedAddress) {
      return bestNormalizedLocationResult.formattedAddress
    }

    return [
      item?.publicPlace,
      item?.number,
      item?.complement,
      item?.neighborhood,
      item?.city,
      item?.state,
      item?.postalCode,
    ]
      .filter(Boolean)
      .join(', ')
  }, [
    bestNormalizedLocationResult?.formattedAddress,
    item?.publicPlace,
    item?.number,
    item?.complement,
    item?.neighborhood,
    item?.city,
    item?.state,
    item?.postalCode,
  ])

  const companyLocationInfoWindowHtml = useMemo(
    () =>
      buildCompanyLocationInfoHtml({
        addressLabel: companyLocationAddressLabel,
        normalizedResult: bestNormalizedLocationResult,
        placesResult: bestPlacesResult,
      }),
    [companyLocationAddressLabel, bestNormalizedLocationResult, bestPlacesResult],
  )

  const openCompanyPanoramaInfoWindow = useCallback(() => {
    if (!google || !streetViewPanoramaInstance || !companyPanoramaMarkerRef.current) {
      return
    }

    let info = companyPanoramaInfoWindowRef.current

    if (info) {
      info.close()
    } else {
      info = new google.maps.InfoWindow()
      companyPanoramaInfoWindowRef.current = info
    }

    info.setOptions({ disableAutoPan: true })
    info.setContent(companyLocationInfoWindowHtml)
    info.open({
      anchor: companyPanoramaMarkerRef.current,
      map: streetViewPanoramaInstance as unknown as google.maps.Map,
    })
  }, [companyLocationInfoWindowHtml, google, streetViewPanoramaInstance])

  useEffect(() => {
    if (!google || !streetViewPanoramaInstance || !streetViewCoordinates) return

    const marker = new google.maps.Marker({
      position: streetViewCoordinates,
      icon: companyMarkerIcon as any,
      zIndex: 200000,
    })

    marker.setMap(streetViewPanoramaInstance as unknown as google.maps.Map)

    marker.addListener('click', () => {
      companyPanoramaViewStateRef.current = null
      closeAllInfoWindows()
      setSelectedAttendanceIndex(null)
      setSelectedAttendancePointOverride(null)
      setIsInitialInfoOpen(prev => !prev)
    })

    companyPanoramaMarkerRef.current = marker

    return () => {
      google.maps.event.clearInstanceListeners(marker)
      marker.setMap(null)

      if (companyPanoramaMarkerRef.current === marker) {
        companyPanoramaMarkerRef.current = null
      }

      companyPanoramaInfoWindowRef.current?.close()
    }
  }, [
    closeAllInfoWindows,
    companyMarkerIcon,
    google,
    streetViewCoordinates,
    streetViewPanoramaInstance,
  ])

  useEffect(() => {
    if (!isStreetViewVisible || !isInitialInfoOpen) {
      companyPanoramaInfoWindowRef.current?.close()
      return
    }

    openCompanyPanoramaInfoWindow()
  }, [isInitialInfoOpen, isStreetViewVisible, openCompanyPanoramaInfoWindow])

  const onLoad = useCallback((streetViewService: google.maps.StreetViewService | null) => {
    if (streetViewService) {
      setStreetViewServiceInstance(streetViewService)
    }
  }, [])

  useEffect(() => {
    if (!google || !streetViewCoordinates) {
      setStatus(undefined)
      return
    }

    const service = streetViewServiceInstance ?? new google.maps.StreetViewService()
    const strictStreetViewSources = getStrictStreetViewSources(google)
    service.getPanorama(
      {
        location: streetViewCoordinates,
        preference: google.maps.StreetViewPreference.NEAREST,
        radius: 80,
        sources: strictStreetViewSources,
      },
      (_data, nextStatus) => {
        setStatus(nextStatus)
      },
    )
  }, [google, streetViewServiceInstance, streetViewCoordinates?.lat, streetViewCoordinates?.lng])

  const getCompanyStreetViewPovForOrigin = useCallback(
    (origin: google.maps.LatLng): google.maps.StreetViewPov | null => {
      if (!google) return null

      if (companyStreetViewLookAtCoordinates) {
        return calculateGooglePovBetweenPositions({
          google,
          origin,
          target: new google.maps.LatLng(companyStreetViewLookAtCoordinates),
        })
      }

      if (pointOnStreetDirectionAngle != null) {
        return {
          heading: pointOnStreetDirectionAngle,
          pitch: 0,
        }
      }

      return null
    },
    [google, companyStreetViewLookAtCoordinates, pointOnStreetDirectionAngle],
  )

  const setPanoramaToLatLng = useCallback(
    ({
      fallbackPointOfView,
      lookAtCoordinates,
      requestedCoordinates,
      targetKey,
      targetType,
    }: {
      fallbackPointOfView?: google.maps.StreetViewPov | null
      lookAtCoordinates: google.maps.LatLngLiteral | null
      requestedCoordinates: google.maps.LatLngLiteral
      targetKey: string
      targetType: StreetViewTarget['type']
    }) => {
      if (!google || !streetViewPanoramaInstance) return
      const service = streetViewServiceInstance ?? new google.maps.StreetViewService()
      const requestId = panoramaRequestIdRef.current + 1
      panoramaRequestIdRef.current = requestId
      streetViewTargetKeyRef.current = targetKey
      const strictStreetViewSources = getStrictStreetViewSources(google)
      const streetViewPreference = google.maps.StreetViewPreference.NEAREST

      logStreetViewLocationDebug('Solicitação de panorama do Street View', {
        legalEntityId: id,
        targetType,
        requestedCoordinates: getDebugCoordinates(requestedCoordinates),
        lookAtCoordinates: getDebugCoordinates(lookAtCoordinates),
        sources: strictStreetViewSources,
        preference: streetViewPreference,
      })

      service.getPanorama(
        {
          location: requestedCoordinates,
          preference: streetViewPreference,
          radius: 80,
          sources: strictStreetViewSources,
        },
        (data, svcStatus) => {
          if (requestId !== panoramaRequestIdRef.current) return

          if (svcStatus === google.maps.StreetViewStatus.OK && data?.location?.pano) {
            const effectiveLookAtLatLng = lookAtCoordinates
            const servicePanoLatLng = (data as any)?.location?.latLng ?? null
            let hasAppliedPointOfView = false
            const stabilizationListeners: google.maps.MapsEventListener[] = []
            const stabilizationTimeoutIds: number[] = []

            const cleanupStabilizationListeners = () => {
              stabilizationListeners.splice(0).forEach(listener => listener.remove())
              stabilizationTimeoutIds.splice(0).forEach(timeoutId => window.clearTimeout(timeoutId))
            }

            const getReadyPanoramaPosition = (allowServiceFallback: boolean) => {
              const actualPanoLatLng = streetViewPanoramaInstance.getPosition() ?? null

              if (!servicePanoLatLng || !actualPanoLatLng) {
                return {
                  actualPanoLatLng,
                  originPanoLatLng: actualPanoLatLng ?? servicePanoLatLng,
                  actualPanoDistanceFromServiceMeters: null,
                  usedServicePanoCoordinatesFallback: false,
                }
              }

              const actualPanoDistanceFromServiceMeters = calculateGoogleLatLngDistanceMeters(
                actualPanoLatLng,
                servicePanoLatLng,
              )

              if (
                actualPanoDistanceFromServiceMeters <=
                STREET_VIEW_PANORAMA_POSITION_TOLERANCE_METERS
              ) {
                return {
                  actualPanoLatLng,
                  originPanoLatLng: actualPanoLatLng,
                  actualPanoDistanceFromServiceMeters,
                  usedServicePanoCoordinatesFallback: false,
                }
              }

              return {
                actualPanoLatLng,
                originPanoLatLng: allowServiceFallback ? servicePanoLatLng : null,
                actualPanoDistanceFromServiceMeters,
                usedServicePanoCoordinatesFallback: allowServiceFallback,
              }
            }

            const applyPointOfView = (
              stabilizationTrigger: string,
              options?: { allowServiceFallback?: boolean },
            ) => {
              if (hasAppliedPointOfView) return

              if (requestId !== panoramaRequestIdRef.current) return

              const {
                actualPanoDistanceFromServiceMeters,
                actualPanoLatLng,
                originPanoLatLng,
                usedServicePanoCoordinatesFallback,
              } = getReadyPanoramaPosition(options?.allowServiceFallback ?? false)

              if ((effectiveLookAtLatLng || fallbackPointOfView) && !originPanoLatLng) {
                logStreetViewLocationDebug('Panorama aguardando posição real do Street View', {
                  legalEntityId: id,
                  source: streetViewLocationSource,
                  stabilizationTrigger,
                  targetType,
                  requestedCoordinates: getDebugCoordinates(requestedCoordinates),
                  servicePanoCoordinates: getDebugGoogleLatLngCoordinates(servicePanoLatLng),
                  actualPanoCoordinates: getDebugGoogleLatLngCoordinates(actualPanoLatLng),
                  actualPanoDistanceFromServiceMeters: getDebugDistanceMeters(
                    actualPanoDistanceFromServiceMeters,
                  ),
                })
                return
              }

              hasAppliedPointOfView = true
              cleanupStabilizationListeners()

              let calculatedPointOfView: google.maps.StreetViewPov | null = null
              let appliedPointOfView: google.maps.StreetViewPov | null = null
              let usedFallbackPointOfView = false
              let distanceToLookAtMeters: number | null = null
              let finalZoom = STREET_VIEW_DEFAULT_ZOOM

              if (originPanoLatLng && effectiveLookAtLatLng) {
                const lookAtGoogleLatLng = new google.maps.LatLng(effectiveLookAtLatLng)
                distanceToLookAtMeters = calculateGoogleLatLngDistanceMeters(
                  originPanoLatLng,
                  lookAtGoogleLatLng,
                )
                calculatedPointOfView = calculateGooglePovBetweenPositions({
                  google,
                  origin: originPanoLatLng,
                  target: lookAtGoogleLatLng,
                })
                appliedPointOfView = {
                  ...calculatedPointOfView,
                  pitch: Math.max(calculatedPointOfView.pitch, STREET_VIEW_MINIMUM_LOOK_AT_PITCH),
                }

                streetViewPanoramaInstance.setPov(appliedPointOfView)
                streetViewPanoramaInstance.setZoom(finalZoom)
              } else if (fallbackPointOfView) {
                appliedPointOfView = fallbackPointOfView
                usedFallbackPointOfView = true
                streetViewPanoramaInstance.setPov(fallbackPointOfView)
                streetViewPanoramaInstance.setZoom(finalZoom)
              }

              streetViewPanoramaInstance.setVisible(true)

              logStreetViewLocationDebug('Panorama resolvido para o Street View', {
                legalEntityId: id,
                source: streetViewLocationSource,
                status: svcStatus,
                stabilizationTrigger,
                targetType,
                requestedCoordinates: getDebugCoordinates(requestedCoordinates),
                servicePanoCoordinates: getDebugGoogleLatLngCoordinates(servicePanoLatLng),
                actualPanoCoordinates: getDebugGoogleLatLngCoordinates(actualPanoLatLng),
                originPanoCoordinates: getDebugGoogleLatLngCoordinates(originPanoLatLng),
                actualPanoDistanceFromServiceMeters: getDebugDistanceMeters(
                  actualPanoDistanceFromServiceMeters,
                ),
                lookAtCoordinates: getDebugCoordinates(effectiveLookAtLatLng),
                distanceToLookAtMeters: getDebugDistanceMeters(distanceToLookAtMeters),
                calculatedPointOfView: getDebugStreetViewPointOfView(calculatedPointOfView),
                finalPointOfView: getDebugStreetViewPointOfView(appliedPointOfView),
                panoramaPointOfViewAfterApply: getDebugStreetViewPointOfView(
                  streetViewPanoramaInstance.getPov(),
                ),
                finalZoom,
                panoramaZoomAfterApply: streetViewPanoramaInstance.getZoom(),
                usedFallbackPointOfView,
                usedServicePanoCoordinatesFallback,
              })

              if (process.env.NODE_ENV !== 'production') {
                window.setTimeout(() => {
                  if (requestId !== panoramaRequestIdRef.current) return

                  logStreetViewLocationDebug('Panorama após estabilização do Street View', {
                    legalEntityId: id,
                    source: streetViewLocationSource,
                    targetType,
                    requestedCoordinates: getDebugCoordinates(requestedCoordinates),
                    actualPanoCoordinates: getDebugGoogleLatLngCoordinates(
                      streetViewPanoramaInstance.getPosition() ?? actualPanoLatLng,
                    ),
                    originPanoCoordinates: getDebugGoogleLatLngCoordinates(originPanoLatLng),
                    lookAtCoordinates: getDebugCoordinates(effectiveLookAtLatLng),
                    expectedPointOfView: getDebugStreetViewPointOfView(appliedPointOfView),
                    panoramaPointOfViewAfterDelay: getDebugStreetViewPointOfView(
                      streetViewPanoramaInstance.getPov(),
                    ),
                    expectedZoom: finalZoom,
                    panoramaZoomAfterDelay: streetViewPanoramaInstance.getZoom(),
                    delayMilliseconds: 1200,
                    usedFallbackPointOfView,
                    usedServicePanoCoordinatesFallback,
                  })
                }, 1200)
              }
            }

            if (effectiveLookAtLatLng || fallbackPointOfView) {
              const schedulePointOfViewApplication = (
                stabilizationTrigger: string,
                delayMilliseconds: number,
                options?: { allowServiceFallback?: boolean },
              ) => {
                const timeoutId = window.setTimeout(
                  () => applyPointOfView(stabilizationTrigger, options),
                  delayMilliseconds,
                )
                stabilizationTimeoutIds.push(timeoutId)
              }

              stabilizationListeners.push(
                google.maps.event.addListenerOnce(streetViewPanoramaInstance, 'pano_changed', () =>
                  schedulePointOfViewApplication('pano_changed', 80),
                ),
              )
              stabilizationListeners.push(
                google.maps.event.addListenerOnce(
                  streetViewPanoramaInstance,
                  'position_changed',
                  () => schedulePointOfViewApplication('position_changed', 50),
                ),
              )
              schedulePointOfViewApplication(
                'timeout',
                STREET_VIEW_PANORAMA_POSITION_FALLBACK_TIMEOUT_MS,
                { allowServiceFallback: true },
              )
            }

            streetViewPanoramaInstance.setPano(data.location.pano)

            if (!effectiveLookAtLatLng && !fallbackPointOfView) {
              applyPointOfView('no_point_of_view')
            }
          } else {
            streetViewPanoramaInstance.setVisible(false)
            setIsStreetViewVisible(false)
          }
        },
      )
    },
    [google, id, streetViewLocationSource, streetViewPanoramaInstance, streetViewServiceInstance],
  )

  const openStreetViewTarget = useCallback(
    (target: StreetViewTarget) => {
      closeAllInfoWindows()
      setIsInitialInfoOpen(false)

      if (target.type === 'attendance') {
        setSelectedAttendanceIndex(target.attendanceIndex)
        setSelectedAttendancePointOverride(target.attendancePoint)
      } else {
        setSelectedAttendanceIndex(null)
        setSelectedAttendancePointOverride(null)
      }

      setIsStreetViewVisible(true)

      if (target.scrollToTop) {
        scrollContentToTop()
      }

      if (streetViewPanoramaInstance) {
        try {
          streetViewPanoramaInstance.setVisible(true)
        } catch {}

        setPanoramaToLatLng({
          fallbackPointOfView: target.type === 'company' ? target.fallbackPointOfView : null,
          lookAtCoordinates: target.lookAtCoordinates,
          requestedCoordinates: target.requestedCoordinates,
          targetKey: getStreetViewTargetKey(target),
          targetType: target.type,
        })
      } else {
        setStreetViewInstanceKey(prev => prev + 1)
      }
    },
    [closeAllInfoWindows, scrollContentToTop, setPanoramaToLatLng, streetViewPanoramaInstance],
  )

  const openAttendanceStreetView = useCallback(
    (index: number, point: AttendanceGeoPoint, options?: { scrollToTop?: boolean }) => {
      openStreetViewTarget({
        type: 'attendance',
        attendanceIndex: index,
        attendancePoint: point,
        requestedCoordinates: point.position,
        lookAtCoordinates: point.position,
        scrollToTop: options?.scrollToTop,
      })
    },
    [openStreetViewTarget],
  )

  const openCompanyStreetView = useCallback(
    (options?: { scrollToTop?: boolean }) => {
      if (!streetViewCoordinates || !google) return

      // Se o Street View não está disponível, volta para o mapa na coordenada original
      if (status === 'ZERO_RESULTS') {
        setIsStreetViewVisible(false)
        setSelectedAttendanceIndex(null)
        setSelectedAttendancePointOverride(null)

        if (mapInstanceRef.current) {
          mapInstanceRef.current.panTo(streetViewCoordinates)
          mapInstanceRef.current.setZoom(16)
        }
        return
      }

      openStreetViewTarget({
        type: 'company',
        requestedCoordinates: streetViewCoordinates,
        lookAtCoordinates: companyStreetViewLookAtCoordinates ?? streetViewCoordinates,
        fallbackPointOfView: getCompanyStreetViewPovForOrigin(
          new google.maps.LatLng(streetViewCoordinates),
        ),
        scrollToTop: options?.scrollToTop,
      })
    },
    [
      companyStreetViewLookAtCoordinates,
      getCompanyStreetViewPovForOrigin,
      google,
      openStreetViewTarget,
      status,
      streetViewCoordinates,
    ],
  )

  useEffect(() => {
    if (!streetViewPanoramaInstance || hasInitializedPov) return
    const effectiveSelectedPoint =
      selectedAttendancePointOverride ??
      (selectedAttendanceIndex != null ? attendanceGeoPoints[selectedAttendanceIndex] : null)

    if (effectiveSelectedPoint && selectedAttendanceIndex != null) {
      openAttendanceStreetView(selectedAttendanceIndex, effectiveSelectedPoint)
    } else {
      openCompanyStreetView()
    }
    setHasInitializedPov(true)
  }, [
    attendanceGeoPoints,
    hasInitializedPov,
    openAttendanceStreetView,
    openCompanyStreetView,
    selectedAttendanceIndex,
    selectedAttendancePointOverride,
    streetViewPanoramaInstance,
  ])

  const handleGoDefaultView = useCallback(() => {
    openCompanyStreetView()
  }, [openCompanyStreetView])

  const handleGoMapView = useCallback(() => {
    if (!streetViewPanoramaInstance) {
      setIsStreetViewVisible(false)
      const effectiveSelectedPoint =
        selectedAttendancePointOverride ??
        (selectedAttendanceIndex != null ? attendanceGeoPoints[selectedAttendanceIndex] : null)
      const target = effectiveSelectedPoint
        ? effectiveSelectedPoint.position
        : streetViewCoordinates
      if (mapInstanceRef.current && target) {
        mapInstanceRef.current.panTo(target)
        mapInstanceRef.current.setZoom(16)
      }
      return
    }
    try {
      streetViewPanoramaInstance.setVisible(false)
    } catch {}
    setIsStreetViewVisible(false)
    try {
      panoramaInfoWindowRef.current?.close()
    } catch {}
    clearAttendancePanoMarkers()
    const effectiveSelectedPoint =
      selectedAttendancePointOverride ??
      (selectedAttendanceIndex != null ? attendanceGeoPoints[selectedAttendanceIndex] : null)
    const target = effectiveSelectedPoint ? effectiveSelectedPoint.position : streetViewCoordinates
    if (mapInstanceRef.current && target) {
      mapInstanceRef.current.panTo(target)
      mapInstanceRef.current.setZoom(16)
    }
  }, [
    streetViewPanoramaInstance,
    clearAttendancePanoMarkers,
    selectedAttendancePointOverride,
    selectedAttendanceIndex,
    attendanceGeoPoints,
    streetViewCoordinates,
  ])

  const handleGoStreetView = useCallback(() => {
    const effectiveSelectedPoint =
      selectedAttendancePointOverride ??
      (selectedAttendanceIndex != null ? attendanceGeoPoints[selectedAttendanceIndex] : null)

    if (effectiveSelectedPoint && selectedAttendanceIndex != null) {
      openAttendanceStreetView(selectedAttendanceIndex, effectiveSelectedPoint)
      return
    }

    openCompanyStreetView()
  }, [
    attendanceGeoPoints,
    openAttendanceStreetView,
    openCompanyStreetView,
    selectedAttendanceIndex,
    selectedAttendancePointOverride,
  ])

  useEffect(() => {
    if (isStreetViewVisible) return
    try {
      if (panoramaInfoWindowRef.current) panoramaInfoWindowRef.current.close()
    } catch {}
    clearAttendancePanoMarkers()
  }, [isStreetViewVisible, clearAttendancePanoMarkers])

  useEffect(() => {
    if (!isStreetViewVisible || !streetViewPanoramaInstance) return

    const effectiveSelectedPoint =
      selectedAttendancePointOverride ??
      (selectedAttendanceIndex != null ? attendanceGeoPoints[selectedAttendanceIndex] : null)

    if (effectiveSelectedPoint && selectedAttendanceIndex != null) {
      const target: StreetViewTarget = {
        type: 'attendance',
        attendanceIndex: selectedAttendanceIndex,
        attendancePoint: effectiveSelectedPoint,
        requestedCoordinates: effectiveSelectedPoint.position,
        lookAtCoordinates: effectiveSelectedPoint.position,
      }

      if (streetViewTargetKeyRef.current === getStreetViewTargetKey(target)) {
        return
      }

      openAttendanceStreetView(selectedAttendanceIndex, effectiveSelectedPoint)
      return
    }

    if (!streetViewCoordinates) return

    const target: StreetViewTarget = {
      type: 'company',
      requestedCoordinates: streetViewCoordinates,
      lookAtCoordinates: companyStreetViewLookAtCoordinates ?? streetViewCoordinates,
    }

    if (streetViewTargetKeyRef.current !== getStreetViewTargetKey(target)) {
      openCompanyStreetView()
    }
  }, [
    attendanceGeoPoints,
    companyStreetViewLookAtCoordinates,
    isStreetViewVisible,
    openAttendanceStreetView,
    openCompanyStreetView,
    selectedAttendanceIndex,
    selectedAttendancePointOverride,
    streetViewCoordinates,
    streetViewPanoramaInstance,
  ])

  useEffect(() => {
    if (!isStreetViewVisible || selectedAttendanceIndex == null) return

    const marker = attendancePanoMarkersReference.current[selectedAttendanceIndex]
    const point =
      selectedAttendancePointOverride ?? attendanceGeoPoints[selectedAttendanceIndex] ?? null

    if (!marker || !point) return

    const timeoutId = window.setTimeout(() => {
      openAttendancePanoramaInfoWindow(marker, point)
    }, 250)

    return () => window.clearTimeout(timeoutId)
  }, [
    attendanceGeoPoints,
    isStreetViewVisible,
    openAttendancePanoramaInfoWindow,
    selectedAttendanceIndex,
    selectedAttendancePointOverride,
  ])

  const selectedAttendancePoint = useMemo(() => {
    if (selectedAttendancePointOverride) return selectedAttendancePointOverride
    if (selectedAttendanceIndex == null) return null
    return attendanceGeoPoints[selectedAttendanceIndex] ?? null
  }, [selectedAttendancePointOverride, selectedAttendanceIndex, attendanceGeoPoints])

  const latLngKey = useCallback(
    (lat: number, lng: number) => `${lat.toFixed(6)},${lng.toFixed(6)}`,
    [],
  )

  const attendancePointIndexByKey = useMemo(() => {
    const map = new Map<string, number>()
    attendanceGeoPoints.forEach((point, index) => {
      map.set(latLngKey(point.position.lat, point.position.lng), index)
    })
    return map
  }, [attendanceGeoPoints, latLngKey])

  const [streetViewAvailabilityByKey, setStreetViewAvailabilityByKey] = useState<
    Map<string, boolean>
  >(new Map())

  useEffect(() => {
    if (!selectedAttendancePointOverride) return

    const selectedPointKey = latLngKey(
      selectedAttendancePointOverride.position.lat,
      selectedAttendancePointOverride.position.lng,
    )

    if (attendancePointIndexByKey.has(selectedPointKey)) return

    setSelectedAttendanceIndex(null)
    setSelectedAttendancePointOverride(null)
  }, [selectedAttendancePointOverride, attendancePointIndexByKey, latLngKey])

  useEffect(() => {
    if (!google) return
    const service = streetViewServiceInstance ?? new google.maps.StreetViewService()
    const strictStreetViewSources = getStrictStreetViewSources(google)
    attendanceGeoPoints.forEach(point => {
      const key = latLngKey(point.position.lat, point.position.lng)
      if (streetViewAvailabilityByKey.has(key)) return
      service.getPanorama(
        {
          location: point.position,
          preference: google.maps.StreetViewPreference.NEAREST,
          radius: 50,
          sources: strictStreetViewSources,
        },
        (_data, svcStatus) => {
          setStreetViewAvailabilityByKey(prev => {
            const next = new Map(prev)
            next.set(key, svcStatus === google.maps.StreetViewStatus.OK)
            return next
          })
        },
      )
    })
  }, [
    google,
    streetViewServiceInstance,
    attendanceGeoPoints,
    latLngKey,
    streetViewAvailabilityByKey,
  ])

  const isStreetViewAvailableForIndex = useCallback(
    (index: number | null | undefined) => {
      if (index == null) return false
      const point = attendanceGeoPoints[index]
      if (!point) return false
      const key = latLngKey(point.position.lat, point.position.lng)
      return streetViewAvailabilityByKey.get(key) === true
    },
    [attendanceGeoPoints, latLngKey, streetViewAvailabilityByKey],
  )

  const attachMapAttendanceInfoWindowClickHandler = useCallback(() => {
    mapAttendanceInfoWindowClickCleanupRef.current?.()
    mapAttendanceInfoWindowClickCleanupRef.current = null

    if (
      selectedAttendanceIndex == null ||
      !selectedAttendancePoint ||
      status === 'ZERO_RESULTS' ||
      !isStreetViewAvailableForIndex(selectedAttendanceIndex)
    ) {
      return
    }

    const attendanceIndex = selectedAttendanceIndex
    const attendancePoint = selectedAttendancePoint
    let clickTarget: Element | null = null
    let clickHandler: ((event: Event) => void) | null = null

    const timeoutId = window.setTimeout(() => {
      const contentElement = document.querySelector<HTMLElement>(
        '[data-attendance-map-info-window="true"]',
      )

      if (!contentElement) return

      clickTarget =
        contentElement.closest('.gm-style-iw-a') ??
        contentElement.closest('.gm-style-iw-t') ??
        contentElement.closest('.gm-style-iw') ??
        contentElement

      clickHandler = event => {
        const eventTarget = event.target instanceof Element ? event.target : null

        if (
          eventTarget?.closest(
            '.gm-ui-hover-effect, button[title="Fechar"], button[aria-label="Close"], button[aria-label="Fechar"]',
          )
        ) {
          return
        }

        if (eventTarget?.closest('[data-attendance-map-info-window="true"]')) {
          return
        }

        openAttendanceStreetView(attendanceIndex, attendancePoint)
      }

      clickTarget.addEventListener('click', clickHandler)
    }, 0)

    mapAttendanceInfoWindowClickCleanupRef.current = () => {
      window.clearTimeout(timeoutId)

      if (clickTarget && clickHandler) {
        clickTarget.removeEventListener('click', clickHandler)
      }
    }
  }, [
    isStreetViewAvailableForIndex,
    openAttendanceStreetView,
    selectedAttendanceIndex,
    selectedAttendancePoint,
    status,
  ])

  useEffect(() => {
    if (!mapInfoWindowRef.current || isStreetViewVisible) return

    attachMapAttendanceInfoWindowClickHandler()
  }, [attachMapAttendanceInfoWindowClickHandler, isStreetViewVisible])

  const formattedAttendanceList = useMemo(() => {
    const items = attendanceList.result?.items
    const attendance = items?.map((item: any) => {
      const imageUrls = Array.isArray(item.imageUrls)
        ? item.imageUrls.filter(
            (imageUrl: unknown): imageUrl is string =>
              typeof imageUrl === 'string' && imageUrl.length > 0,
          )
        : item.imageUrl
          ? [item.imageUrl]
          : []
      const lat = item?.latitude != null ? Number(item.latitude) : NaN
      const lng = item?.longitude != null ? Number(item.longitude) : NaN
      const hasValidCoords = Number.isFinite(lat) && Number.isFinite(lng)
      const pointKey = hasValidCoords ? latLngKey(lat, lng) : null
      const attendancePointIndex = pointKey
        ? (attendancePointIndexByKey.get(pointKey) ?? null)
        : null

      const markerColor =
        attendancePointIndex != null
          ? (attendanceGeoPoints[attendancePointIndex]?.markerColor ?? null)
          : null

      const distanceMeters =
        hasValidCoords && streetViewCoordinates
          ? calculateDistance(
              turf.point([lng, lat]),
              turf.point([streetViewCoordinates.lng, streetViewCoordinates.lat]),
              'kilometers',
            ) * 1000
          : null
      const distanceFormatted = distanceMeters != null ? formatDistance(distanceMeters) : null

      return {
        dataSourceId: item.dataSourceId,
        dataSourceName: item.dataSourceName,
        startDateFormatted:
          item?.startDate === null
            ? null
            : format(parseISO(item.startDate as string), 'dd/MM/yyyy'),
        endDate:
          item?.endDate === null ? null : format(parseISO(item.endDate as string), 'dd/MM/yyyy'),
        agentName: item.agentName ?? null,
        attendanceQuantity: item.attendanceQuantity ?? null,
        justify: item.justify ?? null,
        imageUrl: imageUrls[0] ?? null,
        imageUrls,
        attendancePointIndex,
        markerColor,
        distanceMeters,
        distanceFormatted,
        accuracy: item?.accuracy != null ? Number(item.accuracy) : null,
      }
    })

    return attendance
  }, [
    attendanceList.result,
    attendancePointIndexByKey,
    attendanceGeoPoints,
    latLngKey,
    streetViewCoordinates?.lat,
    streetViewCoordinates?.lng,
  ])

  return (
    <Handle error={error}>
      {isLoading || isLoadingGoogle || !google ? (
        <LoadingWrapper>
          <div className='center'>
            <div className='loader'>
              <PulseLoader color={colors.sebraeBlue} />
            </div>
            <span className='message'>
              <LoadingMessage
                messages={[
                  'Pesquisando por informações da empresa selecionada',
                  'Resgatando dados referentes a empresa selecionada',
                  'Carregando quantitativos da empresa selecionada',
                ]}
              />
            </span>
          </div>
        </LoadingWrapper>
      ) : (
        <>
          <Wrapper ref={contentTopReference}>
            {(isSentinel || isLargeCondition) && !hideHeader && (
              <CardReadOne>
                <CardReadOneTitle>
                  <button onClick={onClose} type='button'>
                    <BackArrowIcon fill='#FFF' />
                  </button>
                  <div className='header-content'>
                    <h3>{String(item?.tradeName ?? item?.corporateName ?? 'Empresa')}</h3>
                    <CardEntitiesListMeta>
                      {item?.cnpj ? (
                        <CardEntitiesListTag>
                          {maskNumbers(String(item.cnpj), masks.cnpj)}
                        </CardEntitiesListTag>
                      ) : null}
                      {cityState !== '-' ? (
                        <CardEntitiesListTag>{cityState}</CardEntitiesListTag>
                      ) : null}
                    </CardEntitiesListMeta>
                  </div>
                </CardReadOneTitle>
              </CardReadOne>
            )}
            <article>
              {isWaitingForPreferredLocation ? (
                <div className='map'>
                  <div className='map-content'>
                    <MapLoadingState style={{ minHeight: '450px' }}>
                      <div className='center'>
                        <div className='loader'>
                          <PulseLoader color={colors.sebraeBlue} />
                        </div>
                        <span className='message'>Atualizando localização da empresa...</span>
                      </div>
                    </MapLoadingState>
                  </div>
                </div>
              ) : streetViewCoordinates ? (
                <div className='map'>
                  <div className='map-content'>
                    <GoogleMap
                      key={`legal-entity-map-${id}`}
                      mapContainerStyle={
                        isSmallCondition
                          ? {
                              width: '100%',
                              height: '350px',
                            }
                          : {
                              width: '100%',
                              height: '450px',
                            }
                      }
                      center={streetViewCoordinates}
                      zoom={15}
                      onLoad={map => {
                        mapInstanceRef.current = map
                      }}
                    >
                      <Marker
                        visible={true}
                        position={streetViewCoordinates}
                        title={String(item?.cnpj ?? '')}
                        icon={companyMarkerIcon as any}
                        zIndex={1000}
                        clickable={!isStreetViewVisible}
                        onClick={() => {
                          if (isStreetViewVisible) return
                          closeAllInfoWindows()
                          setSelectedAttendanceIndex(null)
                          setSelectedAttendancePointOverride(null)
                          setIsInitialInfoOpen(prev => !prev)
                          if (mapInstanceRef.current && streetViewCoordinates) {
                            mapInstanceRef.current.panTo(streetViewCoordinates)
                            mapInstanceRef.current.setZoom(16)
                          }
                        }}
                      />

                      {isInitialInfoOpen && !isStreetViewVisible ? (
                        <InfoWindow
                          position={streetViewCoordinates}
                          onLoad={infoWindow => {
                            mapInitialInfoWindowRef.current = infoWindow
                          }}
                          onUnmount={() => {
                            mapInitialInfoWindowRef.current = null
                          }}
                          onCloseClick={() => setIsInitialInfoOpen(false)}
                        >
                          <CompanyLocationInfo
                            addressLabel={companyLocationAddressLabel}
                            normalizedResult={bestNormalizedLocationResult}
                            placesResult={bestPlacesResult}
                          />
                        </InfoWindow>
                      ) : null}

                      {status === 'ZERO_RESULTS' && !isInitialInfoOpen ? (
                        <InfoWindow position={streetViewCoordinates}>
                          <StreetViewNotAvailableInfo />
                        </InfoWindow>
                      ) : null}

                      {!isStreetViewVisible &&
                        attendanceGeoPoints.map((point, index) => (
                          <Marker
                            key={`attendance-map-${index}`}
                            position={point.position}
                            icon={getAttendanceMarkerIcon(index) as any}
                            visible={true}
                            zIndex={100000 + index}
                            clickable={true}
                            onClick={() => {
                              if (
                                status !== 'ZERO_RESULTS' &&
                                isStreetViewAvailableForIndex(index)
                              ) {
                                openAttendanceStreetView(index, point)
                                return
                              }

                              closeAllInfoWindows()
                              setIsInitialInfoOpen(false)

                              setSelectedAttendanceIndex(index)
                              setSelectedAttendancePointOverride(point)

                              if (mapInstanceRef.current) {
                                mapInstanceRef.current.panTo(point.position)
                                mapInstanceRef.current.setZoom(16)
                              }
                            }}
                          />
                        ))}

                      {selectedAttendancePoint && !isStreetViewVisible ? (
                        <InfoWindow
                          position={selectedAttendancePoint.position}
                          options={{
                            disableAutoPan: true,
                            pixelOffset: new google.maps.Size(0, -36),
                          }}
                          onLoad={infoWindow => {
                            mapInfoWindowRef.current = infoWindow
                            attachMapAttendanceInfoWindowClickHandler()
                          }}
                          onUnmount={() => {
                            mapAttendanceInfoWindowClickCleanupRef.current?.()
                            mapAttendanceInfoWindowClickCleanupRef.current = null
                            mapInfoWindowRef.current = null
                          }}
                          onCloseClick={() => {
                            setSelectedAttendanceIndex(null)
                            setSelectedAttendancePointOverride(null)
                          }}
                        >
                          <AttendanceInfoWindowContent
                            onOpenStreetView={
                              selectedAttendanceIndex != null &&
                              status !== 'ZERO_RESULTS' &&
                              isStreetViewAvailableForIndex(selectedAttendanceIndex)
                                ? () =>
                                    openAttendanceStreetView(
                                      selectedAttendanceIndex,
                                      selectedAttendancePoint,
                                    )
                                : undefined
                            }
                            point={selectedAttendancePoint}
                          />
                        </InfoWindow>
                      ) : null}

                      {selectedAttendancePoint && !isStreetViewVisible ? (
                        <Circle
                          center={selectedAttendancePoint.position}
                          radius={300}
                          options={{
                            strokeColor: selectedAttendancePoint.markerColor,
                            strokeOpacity: 0.9,
                            strokeWeight: 2,
                            fillColor: hexToRgba(selectedAttendancePoint.markerColor, 0.2),
                            fillOpacity: 0.2,
                            clickable: false,
                            draggable: false,
                            editable: false,
                            zIndex: 1,
                          }}
                        />
                      ) : null}

                      {isInitialInfoOpen && !isStreetViewVisible && streetViewCoordinates ? (
                        <Circle
                          center={streetViewCoordinates}
                          radius={300}
                          options={{
                            strokeColor: '#C0392B',
                            strokeOpacity: 0.9,
                            strokeWeight: 2,
                            fillColor: hexToRgba('#C0392B', 0.2),
                            fillOpacity: 0.2,
                            clickable: false,
                            draggable: false,
                            editable: false,
                            zIndex: 1,
                          }}
                        />
                      ) : null}

                      <StreetViewService onLoad={onLoad} />
                      <StreetViewPanorama
                        key={streetViewInstanceKey}
                        onLoad={setStreetViewPanoramaInstance}
                        options={{
                          visible: isStreetViewVisible,
                        }}
                      />
                    </GoogleMap>
                    {status === 'ZERO_RESULTS' && <StreetViewNotAvailableInfo />}

                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        marginBottom: '-15px',
                        width: '100%',
                      }}
                    >
                      {streetViewCoordinates || attendanceGeoPoints.length > 0 ? (
                        <StreetViewControls>
                          <StreetViewButton
                            onClick={handleGoDefaultView}
                            disabled={!streetViewCoordinates}
                          >
                            Visão inicial
                          </StreetViewButton>
                          <StreetViewButton
                            onClick={isStreetViewVisible ? handleGoMapView : handleGoStreetView}
                            disabled={
                              isStreetViewVisible
                                ? !streetViewPanoramaInstance
                                : selectedAttendanceIndex != null
                                  ? !selectedAttendancePoint ||
                                    !isStreetViewAvailableForIndex(selectedAttendanceIndex)
                                  : !streetViewCoordinates || status === 'ZERO_RESULTS'
                            }
                          >
                            {isStreetViewVisible ? 'Ver mapa' : 'Ver Street View'}
                          </StreetViewButton>
                        </StreetViewControls>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}

              <DataFormWrapper>
                <div
                  style={{
                    width: '100%',
                    justifySelf: 'stretch',
                  }}
                >
                  <DataForm
                    item={item}
                    cnae={mainCnae}
                    readOnly={true}
                    useWideSideMarginOnTablet={false}
                  />
                </div>
              </DataFormWrapper>

              <ListWrapper>
                <List
                  title={'Histórico de Atendimento'}
                  items={formattedAttendanceList}
                  isLoading={attendanceList.isLoading}
                  customStyles={{ width: '100%' }}
                  itemsPerPage={3}
                  labels={{
                    resumo: 'Atendimentos',
                  }}
                  fractions={{ resumo: 1 }}
                  hideColumn={['dataSourceName', 'agentName', 'startDateFormatted', 'justify']}
                  customCellPadding={'8px 0px 8px 2px'}
                  customCells={{
                    resumo: (item: any) => {
                      const currentYear = new Date().getFullYear()
                      const startDateParts = item.startDateFormatted
                        ? String(item.startDateFormatted).split('/')
                        : null
                      const startYear =
                        startDateParts && startDateParts.length === 3
                          ? Number(startDateParts[2])
                          : null
                      const isCurrentYear = startYear === currentYear
                      const dotColor = isCurrentYear ? '#27ae60' : '#b1b1b1'
                      const sourceNameDisplay =
                        item.dataSourceName === 'SEBRAE Na Sua Empresa (App V2)'
                          ? 'SEBRAE Na Sua Empresa (App)'
                          : (item.dataSourceName ?? 'Não informado')
                      const agentNameDisplay = item.agentName
                        ? toTitleCase(item.agentName)
                        : 'Não informado'
                      const attendanceQuantityDisplay =
                        item.attendanceQuantity != null
                          ? Number(item.attendanceQuantity).toLocaleString('pt-BR')
                          : 'Não informado'
                      const isMonitorEmissorAttendance =
                        attendanceDataSourceId === MONITOR_EMISSOR_DATA_SOURCE_ID
                      const attendanceSourceNameDisplay = isMonitorEmissorAttendance
                        ? 'Emissor NF-e'
                        : sourceNameDisplay
                      const shouldShowAttendanceLocationDetails = !isMonitorEmissorAttendance
                      const attendanceSecondaryLabel = isMonitorEmissorAttendance
                        ? 'Quantidade de atendimentos'
                        : 'Agente'
                      const attendanceSecondaryDisplay = isMonitorEmissorAttendance
                        ? attendanceQuantityDisplay
                        : agentNameDisplay
                      const attendanceMainColumnSize = shouldShowAttendanceLocationDetails
                        ? 'minmax(0, 1.75fr)'
                        : 'minmax(0, 1fr)'
                      const attendanceLocationColumnSize = 'minmax(140px, 0.95fr)'
                      const attendanceImageUrls = Array.isArray(item.imageUrls)
                        ? item.imageUrls.filter(
                            (imageUrl: unknown): imageUrl is string =>
                              typeof imageUrl === 'string' && imageUrl.length > 0,
                          )
                        : item.imageUrl
                          ? [item.imageUrl]
                          : []
                      const hasAttendanceImage = attendanceImageUrls.length > 0
                      const attendanceButtonColumnSize = hasAttendanceImage ? '86px' : '40px'
                      const desktopAttendanceGridTemplateColumns =
                        shouldShowAttendanceLocationDetails
                          ? `${attendanceMainColumnSize} ${attendanceLocationColumnSize} ${attendanceButtonColumnSize}`
                          : `${attendanceMainColumnSize} ${attendanceButtonColumnSize}`
                      const attendanceGridTemplateColumns = isLargeCondition
                        ? '1fr'
                        : desktopAttendanceGridTemplateColumns
                      const attendanceButtonGridColumn = isLargeCondition
                        ? undefined
                        : shouldShowAttendanceLocationDetails
                          ? '3'
                          : '2'
                      const attendanceLocationGridColumn = isLargeCondition ? '1' : '2'
                      const justifyDisplay = item.justify ?? 'Não informado'

                      return [
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: attendanceGridTemplateColumns,
                            columnGap: isLargeCondition ? 0 : 6,
                            rowGap: 2,
                            alignItems: isLargeCondition ? 'stretch' : 'center',
                            position: 'relative',
                            paddingRight: isLargeCondition ? (hasAttendanceImage ? 98 : 52) : 0,
                          }}
                        >
                          <div
                            style={{
                              gridColumn: '1',
                              gridRow: isLargeCondition ? '1' : '1 / -1',
                              minWidth: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              rowGap: 2,
                              zIndex: 1,
                            }}
                          >
                            <div
                              title={attendanceSourceNameDisplay}
                              style={{
                                minWidth: 0,
                                whiteSpace:
                                  isLargeCondition && !isSmallCondition ? 'normal' : 'nowrap',
                                overflow:
                                  isLargeCondition && !isSmallCondition ? 'visible' : 'hidden',
                                textOverflow:
                                  isLargeCondition && !isSmallCondition ? 'clip' : 'ellipsis',
                              }}
                            >
                              <strong>Fonte:</strong> {attendanceSourceNameDisplay}
                            </div>
                            <div
                              title={attendanceSecondaryDisplay}
                              style={{
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <strong>{attendanceSecondaryLabel}:</strong>
                              &nbsp;
                              <span
                                style={{
                                  minWidth: 0,
                                  flex: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace:
                                    isLargeCondition && !isSmallCondition ? 'normal' : 'nowrap',
                                }}
                              >
                                {attendanceSecondaryDisplay}
                              </span>
                            </div>
                            <div
                              style={{
                                minWidth: 0,
                                whiteSpace: isLargeCondition ? 'normal' : 'nowrap',
                                overflow: isLargeCondition ? 'visible' : 'hidden',
                                textOverflow: isLargeCondition ? 'clip' : 'ellipsis',
                              }}
                            >
                              <strong>Data:</strong>&nbsp;
                              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                {item.startDateFormatted ?? 'Não informado'}
                                <span
                                  style={{
                                    display: 'inline-block',
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: dotColor,
                                    marginLeft: 5,
                                    marginRight: 10,
                                  }}
                                />
                              </span>
                            </div>
                            {item.justify ? (
                              <div
                                title={justifyDisplay}
                                style={{
                                  minWidth: 0,
                                  whiteSpace:
                                    isLargeCondition && !isSmallCondition ? 'normal' : 'nowrap',
                                  overflow:
                                    isLargeCondition && !isSmallCondition ? 'visible' : 'hidden',
                                  textOverflow:
                                    isLargeCondition && !isSmallCondition ? 'clip' : 'ellipsis',
                                }}
                              >
                                <strong>Justificativa:</strong> {justifyDisplay}
                              </div>
                            ) : null}
                          </div>

                          {shouldShowAttendanceLocationDetails ? (
                            <div
                              style={{
                                gridColumn: attendanceLocationGridColumn,
                                gridRow: isLargeCondition ? undefined : '1 / -1',
                                zIndex: 1,
                                alignSelf: isLargeCondition ? undefined : 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                rowGap: 2,
                              }}
                            >
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'baseline',
                                  gap: 4,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <strong>Distância:</strong>
                                <span>{item.distanceFormatted ?? 'Não informado'}</span>
                              </div>
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'baseline',
                                  gap: 4,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <strong>Precisão:</strong>
                                <span>
                                  {item.accuracy != null
                                    ? `${Number(item.accuracy).toLocaleString('pt-BR')} m`
                                    : 'Não informado'}
                                </span>
                              </div>
                            </div>
                          ) : null}

                          {/* Button column */}
                          <div
                            style={{
                              position: isLargeCondition ? 'absolute' : 'static',
                              top: isLargeCondition ? '50%' : undefined,
                              right: isLargeCondition ? 0 : undefined,
                              transform: isLargeCondition ? 'translateY(-50%)' : undefined,
                              gridColumn: attendanceButtonGridColumn,
                              gridRow: isLargeCondition ? undefined : '1 / -1',
                              justifySelf: isLargeCondition ? undefined : 'center',
                              alignSelf: 'center',
                              zIndex: 2,
                              display: 'flex',
                              flexDirection: 'row',
                              gap: 6,
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: attendanceButtonColumnSize,
                            }}
                          >
                            {typeof item.attendancePointIndex === 'number' ? (
                              <button
                                onClick={() => {
                                  if (
                                    status === 'ZERO_RESULTS' ||
                                    typeof item.attendancePointIndex !== 'number' ||
                                    !attendanceGeoPoints[item.attendancePointIndex] ||
                                    !isStreetViewAvailableForIndex(item.attendancePointIndex)
                                  )
                                    return
                                  if (typeof item.attendancePointIndex === 'number') {
                                    const index = item.attendancePointIndex
                                    const target = attendanceGeoPoints[index]
                                    if (target) {
                                      openAttendanceStreetView(index, target, {
                                        scrollToTop: true,
                                      })
                                    }
                                  }
                                }}
                                disabled={
                                  status === 'ZERO_RESULTS' ||
                                  typeof item.attendancePointIndex !== 'number' ||
                                  !attendanceGeoPoints[item.attendancePointIndex] ||
                                  !isStreetViewAvailableForIndex(item.attendancePointIndex)
                                }
                                style={{
                                  height: '2.5rem',
                                  width: '2.5rem',
                                  padding: 0,
                                  border: 'none',
                                  borderRadius: 6,
                                  background: item.markerColor || '#005eb8',
                                  color: '#fff',
                                  cursor:
                                    status === 'ZERO_RESULTS' ||
                                    typeof item.attendancePointIndex !== 'number' ||
                                    !attendanceGeoPoints[item.attendancePointIndex] ||
                                    !isStreetViewAvailableForIndex(item.attendancePointIndex)
                                      ? 'not-allowed'
                                      : 'pointer',
                                  boxShadow: '0px 4px 10px rgba(20, 46, 82, 0.15)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  opacity:
                                    status === 'ZERO_RESULTS' ||
                                    typeof item.attendancePointIndex !== 'number' ||
                                    !attendanceGeoPoints[item.attendancePointIndex] ||
                                    !isStreetViewAvailableForIndex(item.attendancePointIndex)
                                      ? 0.5
                                      : 1,
                                }}
                                title={
                                  status === 'ZERO_RESULTS' ||
                                  typeof item.attendancePointIndex !== 'number' ||
                                  !attendanceGeoPoints[item.attendancePointIndex] ||
                                  !isStreetViewAvailableForIndex(item.attendancePointIndex)
                                    ? 'Sem Street View'
                                    : 'Ver no Street View'
                                }
                                aria-label={
                                  status === 'ZERO_RESULTS' ||
                                  typeof item.attendancePointIndex !== 'number' ||
                                  !attendanceGeoPoints[item.attendancePointIndex] ||
                                  !isStreetViewAvailableForIndex(item.attendancePointIndex)
                                    ? 'Sem Street View'
                                    : 'Ver no Street View'
                                }
                              >
                                <MapPinIcon width={18} height={18} fill={'#fff'} />
                              </button>
                            ) : null}
                            {hasAttendanceImage ? (
                              <button
                                onClick={() => {
                                  setIsAttendanceImageLoading(true)
                                  setSelectedAttendanceImages({
                                    urls: attendanceImageUrls,
                                    index: 0,
                                  })
                                }}
                                style={{
                                  height: '2.5rem',
                                  width: '2.5rem',
                                  padding: 0,
                                  border: 'none',
                                  borderRadius: 6,
                                  background: `${complementarColors.orange}`,
                                  color: '#fff',
                                  cursor: 'pointer',
                                  boxShadow: '0px 4px 10px rgba(20, 46, 82, 0.15)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                                title='Ver imagem do atendimento'
                                aria-label='Ver imagem do atendimento'
                              >
                                <AttendanceImageIcon width={18} height={18} fill={'#fff'} />
                              </button>
                            ) : null}
                          </div>
                        </div>,
                      ].filter(Boolean)
                    },
                  }}
                />
              </ListWrapper>

              {selectedAttendanceImageUrl ? (
                <Modal
                  isOpen={Boolean(selectedAttendanceImageUrl)}
                  onClose={closeSelectedAttendanceImages}
                  relativeCloseButton
                  customStyles={{
                    content: {
                      position: 'absolute',
                      inset: '50% auto auto 50%',
                      transform: 'translate(-50%, -50%)',
                      width: 'auto',
                      height: 'auto',
                      minWidth: '400px',
                      maxHeight: '85vh',
                      padding: 0,
                      border: 'none',
                      borderRadius: 8,
                      background: '#fff',
                    },
                    overlay: {
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 20,
                    },
                  }}
                >
                  <AttendanceImageModalContent>
                    {isAttendanceImageLoading ? (
                      <AttendanceImageLoader>
                        <PulseLoader color={colors.sebraeBlue} />
                      </AttendanceImageLoader>
                    ) : null}
                    {selectedAttendanceImageCount > 1 ? (
                      <AttendanceImageNavigationButton
                        $side='left'
                        onClick={() => goToSelectedAttendanceImage(-1)}
                        type='button'
                        aria-label='Imagem anterior'
                        title='Imagem anterior'
                      >
                        <LeftArrowIcon />
                      </AttendanceImageNavigationButton>
                    ) : null}
                    <AttendanceImage
                      key={selectedAttendanceImageUrl}
                      src={selectedAttendanceImageUrl}
                      alt={`Imagem do atendimento ${selectedAttendanceImages ? selectedAttendanceImages.index + 1 : 1} de ${selectedAttendanceImageCount}`}
                      $isLoading={isAttendanceImageLoading}
                      onLoad={() => setIsAttendanceImageLoading(false)}
                      onError={() => setIsAttendanceImageLoading(false)}
                    />
                    {selectedAttendanceImageCount > 1 ? (
                      <>
                        <AttendanceImageNavigationButton
                          $side='right'
                          onClick={() => goToSelectedAttendanceImage(1)}
                          type='button'
                          aria-label='Próxima imagem'
                          title='Próxima imagem'
                        >
                          <RightArrowIcon />
                        </AttendanceImageNavigationButton>
                        <AttendanceImageCounter>
                          {(selectedAttendanceImages?.index ?? 0) + 1} /{' '}
                          {selectedAttendanceImageCount}
                        </AttendanceImageCounter>
                      </>
                    ) : null}
                  </AttendanceImageModalContent>
                </Modal>
              ) : null}

              <ListWrapper>
                <List
                  title={'Histórico de Atendimento - Pessoa Física'}
                  items={formattedLegalEntityIndividualList}
                  isLoading={legalEntityIndividualList.isLoading}
                  customStyles={{ width: '100%', fontSize: '1rem' }}
                  labels={{
                    name: 'Nome',
                    relationship: 'Relação',
                  }}
                />
              </ListWrapper>

              <ListWrapper style={{ gridColumn: !isLargeCondition ? 'span 2' : 'span 1' }}>
                <List
                  title={'Cursos EAD'}
                  items={formattedCourseRegisterIndividualList}
                  isLoading={legalEntityIndividualList.isLoading}
                  customStyles={{ width: '100%', fontSize: '1rem' }}
                  itemsPerPage={5}
                  labels={
                    isLargeCondition
                      ? { resumo: 'Cursos EAD' }
                      : {
                          courseProductCode: 'AVA',
                          courseProductName: 'Nome',
                          courseProductType: 'Tipo',
                          isPortfolio: 'Portfólio',
                          courseCompletionPercentage: 'Conclusão',
                          registerDate: 'Data',
                        }
                  }
                  fractions={
                    isLargeCondition
                      ? { resumo: 1 }
                      : {
                          courseProductCode: 0.15,
                          courseProductName: 0.35,
                          courseProductType: 0.15,
                          courseCompletionPercentage: 0.15,
                          isPortfolio: 0.15,
                          registerDate: 0.15,
                        }
                  }
                  customCellPadding={isLargeCondition ? '8px 0px 8px 2px' : undefined}
                  customCells={
                    isLargeCondition
                      ? {
                          resumo: (item: any) => (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: isSmallCondition ? '1fr' : '1fr 1fr',
                                columnGap: '12px',
                                rowGap: '6px',
                                alignItems: 'start',
                                width: '100%',
                              }}
                            >
                              <div>
                                <strong>AVA:</strong> {item.courseProductCode}
                              </div>
                              <div>
                                <strong>Nome:</strong> {item.courseProductName}
                              </div>
                              <div>
                                <strong>Tipo:</strong> {item.courseProductType}
                              </div>
                              <div>
                                <strong>Portfólio Atual:</strong> {item.isPortfolio}
                              </div>
                              <div>
                                <strong>Conclusão:</strong> {item.courseCompletionPercentage}
                              </div>
                              <div>
                                <strong>Data de Matrícula:</strong> {item.registerDate}
                              </div>
                            </div>
                          ),
                        }
                      : null
                  }
                />
              </ListWrapper>
            </article>
          </Wrapper>
        </>
      )}
    </Handle>
  )
}

const StreetViewNotAvailableInfo = () => {
  return (
    <StreetViewInfoContainer>
      <AlertIcon width={18} height={16} fill={'#f1c40f'} />
      <StreetViewInfoContent>
        Dados de StreetView indisponíveis para este endereço.
      </StreetViewInfoContent>
    </StreetViewInfoContainer>
  )
}

const CompanyLocationInfo = ({
  addressLabel,
  normalizedResult,
  placesResult,
}: {
  addressLabel?: string
  normalizedResult?: CompanyNormalizedLocationResult | null
  placesResult?: CompanyPlacesResult | null
}) => {
  const info = getCompanyLocationInfoData({
    addressLabel,
    normalizedResult,
    placesResult,
  })

  return (
    <CompanyLocationInfoWindowContainer>
      <CompanyLocationInfoWindowTitle>{info.title}</CompanyLocationInfoWindowTitle>

      {info.addressLabel ? (
        <CompanyLocationInfoWindowRow>
          <strong>Endereço:</strong> {info.addressLabel}
        </CompanyLocationInfoWindowRow>
      ) : null}

      {info.confidenceText ? (
        <CompanyLocationInfoWindowRow>
          <strong>Confiança:</strong> {info.confidenceText}
        </CompanyLocationInfoWindowRow>
      ) : null}

      {info.sourceLabel ? (
        <CompanyLocationInfoWindowRow>
          <strong>Fonte:</strong> {info.sourceLabel}
        </CompanyLocationInfoWindowRow>
      ) : null}

      {info.primaryType ? (
        <CompanyLocationInfoWindowRow>
          <strong>Tipo:</strong> {info.primaryType}
        </CompanyLocationInfoWindowRow>
      ) : null}

      {info.businessStatus ? (
        <CompanyLocationInfoWindowRow>
          <strong>Status:</strong> {info.businessStatus}
        </CompanyLocationInfoWindowRow>
      ) : null}

      {info.googleMapsUri ? (
        <CompanyLocationInfoWindowLink href={info.googleMapsUri} target='_blank' rel='noreferrer'>
          Abrir no Google Maps
        </CompanyLocationInfoWindowLink>
      ) : null}
    </CompanyLocationInfoWindowContainer>
  )
}
